import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Platform } from './Icons.jsx';
import i18n from '../i18n';
import { api } from '../services/api/client.js';
import { useContentFeed } from '../hooks/useContentFeed.js';
import { normalizePost } from './preview/normalizePost.js';
import { useGenerationStatus } from '../state/GenerationStatusContext.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import CreationAccessNotice, { creationAccessState } from './billing/CreationAccessNotice.jsx';
import { deriveAssistantAllowance, assistantGate } from '../lib/assistantAllowance.js';
import { trackAssistantResult } from '../lib/operations.js';
import { platformDisplayName } from '../lib/platforms.js';
import {
  assistantActionLocale,
  assistantActionText,
  assistantActionsForContext,
  assistantTrayText,
  buildAssistantActionDraft,
} from './assistant/actionRegistry.js';

const ASSISTANT_NAME = 'Dathent Assistant';
const ASSISTANT_OAUTH_RETURN_KEY = 'dathent_assistant_social_oauth_return';

const SOCIAL_PLATFORM_LABELS = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  threads: 'Threads',
  telegram: 'Telegram',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  x: 'X',
};

const makeMessageId = (prefix) => `${prefix}-${globalThis.crypto?.randomUUID?.() || Date.now()}`;

// Returns the first sentence of `text` trimmed to ~48 characters, or null if
// the text is missing or that first sentence is too long to make a clean
// status pill. Used for AI/locale-driven result-card titles.
const firstShortSentence = (text) => {
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  const sentence = trimmed.split(/[.!?\n]/)[0].trim();
  if (!sentence) return null;
  if (sentence.length > 48) return null;
  return sentence;
};
const ASSISTANT_PANEL_MESSAGES_KEY = 'dathent_assistant_panel_messages_v1';

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(reader.error || new Error(i18n.t('assistant.ui.couldNotReadFile', 'Could not read file')));
  reader.readAsDataURL(file);
});

const greetingMessage = (screen, language = assistantUiLanguage()) => {
  const tt = i18n.getFixedT(assistantLanguage(language));
  return {
    id: makeMessageId('greeting'),
    from: 'ai',
    text: tt(`assistant.greetings.${screen}`, tt('assistant.greetings.default')),
  };
};

const loadCachedAssistantPanelMessages = () => {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(ASSISTANT_PANEL_MESSAGES_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter(message => message && typeof message === 'object').slice(-50) : [];
  } catch {
    return [];
  }
};

const cacheAssistantPanelMessages = (messages) => {
  if (typeof window === 'undefined' || !hasConversationMessages(messages)) return;
  try {
    window.localStorage.setItem(ASSISTANT_PANEL_MESSAGES_KEY, JSON.stringify(messages.slice(-50)));
  } catch {
    // Best-effort cache only. Server history remains the source of truth.
  }
};

const hasConversationMessages = (messages) => messages.some(message => (
  message.persisted || message.from === 'user' || message.response || message.error
));

const persistedMessageToBubble = (message) => {
  const isAssistant = message.role === 'assistant';
  const response = isAssistant && message.response ? {
    message: message.message,
    intent: message.response.intent,
    requiresConfirmation: Boolean(message.response.requiresConfirmation),
    creditCost: Number(message.response.creditCost || 0),
    freeQuotaUsed: Boolean(message.response.freeQuotaUsed),
    freeMessagesRemaining: message.response.freeMessagesRemaining,
    operationType: message.response.operationType || 'assistant.chat',
    proposedActions: Array.isArray(message.response.proposedActions) ? message.response.proposedActions : [],
    quickReplies: Array.isArray(message.response.quickReplies) ? message.response.quickReplies : [],
    quickReplyGroup: message.response.quickReplyGroup || null,
    safeToExecute: Boolean(message.response.safeToExecute),
    resultCard: message.response.resultCard || null,
    responseLanguage: message.response.responseLanguage || null,
  } : null;

  return {
    id: message.id || makeMessageId(isAssistant ? 'ai' : 'user'),
    from: isAssistant ? 'ai' : 'user',
    text: message.message || '',
    response,
    resultCard: response?.resultCard || null,
    persisted: true,
  };
};

const assistantResponseToBubble = (assistantResponse, fallbackText = 'AI response ready.') => ({
  id: makeMessageId('ai'),
  from: 'ai',
  text: assistantResponse?.message || fallbackText,
  response: assistantResponse ? {
    ...assistantResponse,
    proposedActions: Array.isArray(assistantResponse.proposedActions) ? assistantResponse.proposedActions : [],
  } : null,
  resultCard: assistantResponse?.resultCard || null,
});

const assistantQuickReplies = (response) => Array.isArray(response?.quickReplies)
  ? response.quickReplies.filter(reply => reply && typeof reply === 'object' && (reply.prompt || reply.label)).slice(0, 6)
  : [];

const assistantQuickReplyGroup = (response) => {
  const group = response?.quickReplyGroup;
  if (!group || group.type !== 'platform_picker' || !Array.isArray(group.options)) return null;
  const options = group.options
    .filter(option => option && typeof option === 'object' && option.id && (option.label || option.prompt))
    .slice(0, 8);
  return options.length ? { ...group, options } : null;
};

const quickReplyActionMetadata = (reply) => {
  if (!reply?.params || typeof reply.params !== 'object' || Array.isArray(reply.params)) return null;
  return {
    actionId: reply.actionId || null,
    type: reply.type || null,
    label: reply.label || reply.prompt || null,
    params: reply.params,
  };
};

const assistantUsageText = (response, action = null) => {
  if (!response && !action) return null;
  if (response?.resultCard) return null;
  if (response?.resultCard?.result?.status === 'insufficient_credits') return null;
  const cost = Number(action?.creditCost ?? 0);
  if (cost > 0) return `${cost} ${cost === 1 ? 'quota unit' : 'quota units'}`;
  return null;
};

// Every locale that ships a common.json (the assistant.* namespace lives there).
const SUPPORTED_ASSISTANT_LANGS = ['uk', 'ru', 'en', 'de', 'es', 'pl', 'cs', 'lt', 'lv', 'sv'];
const assistantLanguage = (value) => SUPPORTED_ASSISTANT_LANGS.includes(value) ? value : 'en';

// Response-language copy (input placeholders, result-card labels): rendered in
// the conversation language via getFixedT, so it matches what the user typed in.
const assistantCopy = (language, key) => i18n.getFixedT(assistantLanguage(language))(`assistant.copy.${key}`);

const assistantUiLanguage = () => {
  const active = String(i18n.language || '').slice(0, 2).toLowerCase();
  if (SUPPORTED_ASSISTANT_LANGS.includes(active)) return active;
  if (typeof navigator === 'undefined') return 'en';
  const language = String(navigator.language || '').slice(0, 2).toLowerCase();
  return SUPPORTED_ASSISTANT_LANGS.includes(language) ? language : 'en';
};

const assistantInputPlaceholder = (metadata, language = assistantUiLanguage()) => {
  const id = metadata?.actionId;
  const type = metadata?.type;
  if (id === 'generate_week' || type === 'content.generate_paid_batch') return assistantCopy(language, 'placeholderBatch');
  if (id === 'copy_rewrite_post' || type === 'content.rewrite_for_platform') return assistantCopy(language, 'placeholderRewrite');
  if (id === 'from_reference' || type === 'content.create_post_from_reference') return assistantCopy(language, 'placeholderReference');
  if (id === 'create_post' || type === 'content.generate_single_draft') return assistantCopy(language, 'placeholderPost');
  return assistantCopy(language, 'placeholderDefault');
};

const localizedCredits = (value, language = 'en') => {
  const amount = Number(value || 0);
  if (language === 'uk') return `${amount} ${amount === 1 ? 'одиниця квоти' : amount >= 2 && amount <= 4 ? 'одиниці квоти' : 'одиниць квоти'}`;
  if (language === 'ru') return `${amount} ${amount === 1 ? 'единица квоты' : 'единиц квоты'}`;
  return `${amount} ${amount === 1 ? 'quota unit' : 'quota units'}`;
};

const createdDraftCount = (payload = {}) => {
  if (Array.isArray(payload.createdContentIds)) return payload.createdContentIds.length;
  if (payload.createdContentId) return 1;
  return 0;
};

const createdContentIdsFromAssistantResult = (resultLike) => {
  const payloads = [
    resultLike?.resultCard?.result,
    resultLike?.result,
    resultLike,
  ].filter(Boolean);
  const ids = [];
  payloads.forEach(payload => {
    if (Array.isArray(payload.createdContentIds)) ids.push(...payload.createdContentIds);
    if (Array.isArray(payload.createdDrafts)) ids.push(...payload.createdDrafts.map(draft => draft?.contentId || draft?.content?.id));
    if (payload.createdContentId) ids.push(payload.createdContentId);
    if (payload.contentId) ids.push(payload.contentId);
  });
  return [...new Set(ids.filter(Boolean))];
};

const generationBatchIdFromAssistantResult = (resultLike) => {
  const payloads = [
    resultLike?.resultCard?.result,
    resultLike?.result,
    resultLike,
  ].filter(Boolean);
  for (const payload of payloads) {
    if (typeof payload.batchId === 'string' && payload.batchId) return payload.batchId;
    if (typeof payload.generationBatchId === 'string' && payload.generationBatchId) return payload.generationBatchId;
    const redirectUrl = String(payload.redirectUrl || payload.previewUrl || '');
    const match = redirectUrl.match(/[?&]batch=([^&]+)/);
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1]);
      } catch {
        return match[1];
      }
    }
  }
  return null;
};

const assistantResultLanguage = (result, fallback = 'en') => assistantLanguage(
  result?.responseLanguage || result?.assistantResponse?.responseLanguage || result?.result?.responseLanguage || fallback
);

const assistantCreditReceipt = (payload = {}, result = {}, language = 'en') => {
  const charged = Number(payload.creditsCharged ?? result?.creditsCharged ?? 0);
  const balance = payload.creditBalance || result?.creditBalance || null;
  if (charged > 0) {
    const remaining = balance?.remaining;
    return Number.isFinite(Number(remaining)) && Number(remaining) <= 3
      ? `${localizedCredits(Number(remaining), language)} ${assistantCopy(language, 'left')}`
      : null;
  }
  return null;
};

const firstUsableImageUrl = (...sources) => {
  for (const source of sources) {
    if (typeof source === 'string' && /^https?:\/\//i.test(source)) return source;
    if (Array.isArray(source)) {
      const found = firstUsableImageUrl(...source);
      if (found) return found;
    }
  }
  return null;
};

const createdDraftItems = (payload = {}) => {
  if (Array.isArray(payload.createdDrafts) && payload.createdDrafts.length) return payload.createdDrafts;
  if (payload.createdContentId || payload.contentId || payload.draft || payload.content) {
    return [{
      contentId: payload.createdContentId || payload.contentId,
      contentVersionId: payload.contentVersionId,
      platform: payload.platform || payload.content?.platform || payload.draft?.targetPlatform || payload.draft?.platform,
      title: payload.title || payload.content?.title || payload.draft?.title,
      previewText: payload.previewText || payload.content?.previewText || payload.caption || payload.draft?.caption,
      openUrl: payload.openUrl || payload.previewUrl || payload.redirectUrl,
      redirectUrl: payload.redirectUrl,
      mediaReuseStatus: payload.mediaReuseStatus || payload.mediaSummary || payload.draft?.mediaReuseStatus,
      mediaSummary: payload.mediaSummary,
      content: payload.content,
      draft: payload.draft,
    }];
  }
  return [];
};

const platformListLabel = (platforms = []) => {
  const values = [...new Set((platforms || []).filter(Boolean))];
  return values.map(socialPlatformLabel).join(', ');
};

const actionSummary = (action) => {
  if (!action) return i18n.t('assistant.ui.noActionAvailable', 'No action available.');
  const summary = action.summary || action.params?.inputSummary || action.params?.summary;
  return summary || action.type?.split('.').join(' ') || i18n.t('assistant.ui.reviewThisAction', 'Review this action');
};

const actionPlatform = (action) => {
  const params = action?.params || {};
  const first = Array.isArray(params.platforms) ? params.platforms[0] : null;
  return String(params.platform || params.targetPlatform || first || '').toLowerCase() || null;
};

const actionNeedsConfirmation = (response, action) => Boolean(
  action?.requiresConfirmation
  || response?.requiresConfirmation
  || Number(action?.creditCost ?? response?.creditCost ?? 0) > 0
  || ['brand_profile.suggest_update', 'brand_profile.apply_update', 'strategy.suggest_update', 'strategy.apply_update', 'content.rewrite_draft', 'content.generate_single_draft', 'content.collect_reference', 'content.analyze_reference', 'content.generate_paid_batch', 'visual.generate_premium_image', 'scheduler.schedule_post', 'publisher.publish_now'].includes(action?.type)
);

const actionResultView = (action, result, language = 'en') => {
  if (!result) return null;
  const payload = result.result || {};
  const createdCount = createdDraftCount(payload);
  const isGenerationSession = Boolean(payload.batchId || String(payload.redirectUrl || '').includes('batch='));
  const tt = i18n.getFixedT(assistantLanguage(language));
  const baseMessage = result.message || (result.executed ? tt('assistant.ui.reviewResult', 'Review the result before applying anything else.') : tt('assistant.ui.noChanges', 'No changes were made.'));
  const message = isGenerationSession
    ? baseMessage
    : createdCount > 0
    ? tt('assistant.result.savedNothingScheduled', '{{count}} saved to your Library. Nothing was scheduled or published.', { count: createdCount })
    : baseMessage;
  const type = action?.type || result.action?.type;
  const redirectUrl = payload.redirectUrl || payload.firstContentUrl || payload.linkUrl || result.redirectUrl || result.linkUrl;
  if (payload.status === 'insufficient_credits') {
    return {
      message: result.message || payload.message || tt('assistant.ui.planQuotaShort', 'Plan quota is not enough. No changes were made.'),
      label: tt('assistant.result.upgradePlan', 'Upgrade plan'),
      redirectUrl: payload.upgradeUrl || '/app/settings?tab=billing'
    };
  }
  if (redirectUrl) return { message, label: isTelegramUrl(redirectUrl) ? tt('assistant.result.openTelegram', 'Open Telegram') : isOAuthStartUrl(redirectUrl) ? tt('assistant.result.continueOAuth', 'Continue OAuth') : payload.batchId ? tt('assistant.result.openGeneration', 'Open generation') : createdCount > 0 ? tt('assistant.result.openFirstDraft', 'Open first draft') : redirectUrl.startsWith('http') ? tt('assistant.result.openResult', 'Open result') : tt('assistant.result.openInApp', 'Open in app'), redirectUrl };
  if (payload.createdContentId) return { message, label: tt('assistant.result.openLibrary', 'Open Library'), screen: 'content' };
  if (type === 'content.suggest_ideas') return { message: result.message || tt('assistant.result.preparedIdeas', 'I prepared content ideas for review.'), label: tt('assistant.result.viewIdeas', 'View ideas'), screen: 'create' };
  if (type === 'visual.suggest_prompt') return { message: result.message || tt('assistant.result.preparedVisualPrompt', 'I prepared a visual prompt for review.'), label: tt('assistant.result.viewPost', 'View post'), screen: 'preview' };
  if (type === 'content.rewrite_draft' || type === 'content.generate_single_draft') return { message, label: tt('assistant.result.openEditor', 'Open editor'), screen: 'editor' };
  if (type === 'strategy.regenerate_content_plan') return { message, label: tt('assistant.result.viewContentPlan', 'View Content Plan'), screen: 'brand' };
  if (type?.startsWith('strategy.') || type === 'brand_profile.suggest_update') return { message, label: tt('assistant.result.viewStrategy', 'View strategy'), screen: 'brand' };
  if (type === 'content.collect_reference' || type === 'content.analyze_reference') return { message, label: tt('assistant.result.viewReferences', 'View references'), screen: 'brand' };
  if (type === 'scheduler.schedule_post') return { message, label: tt('assistant.result.openScheduler', 'Open scheduler'), screen: 'scheduler' };
  return { message, label: null, screen: null };
};

const previewPayload = (previewResult) => {
  const result = previewResult?.result || previewResult;
  if (!result) return null;
  if (result.analysis || result.prompt || result.profileUpdate || result.appliedGuidance) return result;
  return result.preview || result.draft || result.asset || result.post || result;
};

const socialPlatformLabel = (platform) => SOCIAL_PLATFORM_LABELS[String(platform || '').toLowerCase()] || String(platform || 'Platform');

const isOAuthStartUrl = (url) => String(url || '').toLowerCase().includes('/oauth/') && String(url || '').toLowerCase().includes('/start');
const isTelegramUrl = (url) => /^https?:\/\/t\.me\//i.test(String(url || '')) || /^https?:\/\/telegram\.me\//i.test(String(url || ''));

const isSocialAccountsPayload = (payload) => Boolean(
  payload
  && (
    Array.isArray(payload.connectedPlatforms)
    || Array.isArray(payload.needsAttentionPlatforms)
    || Array.isArray(payload.notConnectedPlatforms)
    || Array.isArray(payload.availableProviders)
  )
);

const storeAssistantOAuthReturn = ({ url, action, result, platform }) => {
  if (typeof window === 'undefined' || !isOAuthStartUrl(url)) return;
  const payload = result?.result || result || {};
  const context = payload.oauthReturnContext || payload.platformStatus?.oauthReturnContext || {};
  try {
    window.localStorage.setItem(ASSISTANT_OAUTH_RETURN_KEY, JSON.stringify({
      source: 'assistant',
      platform: context.platform || platform || payload.platform || action?.params?.platform || null,
      actionType: context.actionType || action?.type || result?.action?.type || null,
      currentScreen: context.currentScreen || action?.params?.currentScreen || null,
      returnPath: context.returnPath || '/settings?tab=social',
      status: payload.status || null,
      at: Date.now(),
    }));
  } catch {
    // OAuth can continue without local return context.
  }
};

const openResultUrl = (url, onNavigate, context = {}) => {
  if (!url) return;
  storeAssistantOAuthReturn({ ...context, url });
  const internalUrl = internalResultUrl(url);
  if (internalUrl?.startsWith('/api/')) {
    window.location.assign(api.url(internalUrl));
    return;
  }
  if (internalUrl) {
    const [rawPath] = internalUrl.split(/[?#]/);
    const path = rawPath.replace(/^\/app(?=\/|$)/, '');
    // Path-style batch URL: /app/batch/:id (current backend format).
    const pathBatchId = batchIdFromPath(path);
    if (pathBatchId && typeof onNavigate === 'function') {
      onNavigate('batch', { batchId: pathBatchId, keepAssistantOpen: true });
      return;
    }
    // Legacy query-style: /create?batch=:id or /posts/create?batch=:id.
    const batchId = generationBatchIdFromUrl(internalUrl);
    if (batchId && (path === '/create' || path === '/posts/create') && typeof onNavigate === 'function') {
      onNavigate('create', { batchId, keepAssistantOpen: true });
      return;
    }
    // Path-style post URL: /app/post/:id (current canonical) + legacy /content/:id/preview.
    const contentId = contentIdFromPath(path);
    if (contentId && typeof onNavigate === 'function') {
      onNavigate('preview', { contentId, keepAssistantOpen: true });
      return;
    }
    const screen = screenForResultPath(path);
    if (typeof onNavigate === 'function') {
      onNavigate(screen);
    } else {
      window.location.assign(url);
    }
    return;
  }
  window.location.assign(url);
};

const generationBatchIdFromUrl = (url = '') => {
  const match = String(url).match(/[?&]batch=([^&]+)/);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const internalResultUrl = (url) => {
  const value = String(url || '');
  if (value.startsWith('/')) return value;
  if (typeof window === 'undefined') return null;
  try {
    const parsed = new URL(value);
    if (parsed.origin !== window.location.origin) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

// Path-style batch URL like /batch/gb_abc (after stripping /app prefix).
const batchIdFromPath = (path) => {
  const match = String(path || '').match(/^\/batch\/([^/?#]+)$/i);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

// Canonical /post/:id (current) + legacy /content/:id/preview. Both
// resolve to a single content_item; handleNavigate routes via
// 'preview' + { contentId }.
const contentIdFromPath = (path) => {
  const value = String(path || '');
  const canonical = value.match(/^\/post\/([^/?#]+)$/i);
  if (canonical?.[1]) {
    try { return decodeURIComponent(canonical[1]); } catch { return canonical[1]; }
  }
  const legacy = value.match(/^\/content\/([^/]+)\/preview$/i);
  if (legacy?.[1]) {
    try { return decodeURIComponent(legacy[1]); } catch { return legacy[1]; }
  }
  return null;
};

const contentPreviewIdFromPath = (path) => {
  const match = String(path || '').match(/^\/content\/([^/]+)\/preview$/i);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const screenForResultPath = (path) => {
  const normalized = String(path || '').replace(/^\//, '') || 'dashboard';
  if (normalized === 'content-plan' || normalized === 'brand') return 'brand';
  if (normalized === 'settings/billing' || normalized === 'billing') return 'billing';
  if (normalized === 'posts/library' || normalized === 'content') return 'content';
  if (normalized === 'posts/create' || normalized === 'create') return 'create';
  if (normalized === 'posts/calendar' || normalized === 'scheduler') return 'scheduler';
  return normalized;
};

const referenceFollowUpPrompt = (prompt, assetIds) => {
  const ids = (Array.isArray(assetIds) ? assetIds : [assetIds]).filter(Boolean);
  if (!ids.length) return prompt;
  const noun = ids.length === 1 ? 'asset' : 'assets';
  return `${prompt}\nUse reference ${noun} ${ids.join(', ')}.`;
};

const extractFirstHttpUrl = (value = '') => {
  const match = String(value).match(/https?:\/\/[^\s<>()"']+/i);
  return match ? match[0].replace(/[.,;:!?]+$/, '') : null;
};

const MAX_ASSISTANT_IMAGES = 8;
const ASSISTANT_MODAL_FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const isReferenceImageFile = (file) => Boolean(file && /^image\//i.test(file.type || '') && file.size <= 8 * 1024 * 1024);

const assistantReferenceContextFromResult = (attachment = {}, result = {}) => {
  const asset = result?.asset || {};
  const assetId = asset.id || asset.assetId || null;
  const publicUrl = asset.publicUrl || attachment.publicUrl || null;
  if (!assetId && !publicUrl) return null;
  const label = attachment.originalFilename || asset.originalFilename || publicUrl || attachment.type || 'Reference';
  return {
    id: assetId || publicUrl,
    assetId,
    publicUrl,
    type: asset.type || attachment.type || 'reference',
    label: compactReferenceLabel(label),
    saved: Boolean(assetId),
  };
};

const compactReferenceLabel = (value = '') => {
  const text = String(value || 'Reference').replace(/^https?:\/\//i, '').replace(/^www\./i, '').trim();
  return text.length > 34 ? `${text.slice(0, 31)}...` : text;
};

const compactDraftTitle = (value = '') => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text || /^Generating:/i.test(text)) return '';
  return text.length > 92 ? `${text.slice(0, 89).trim()}...` : text;
};

const referenceActionDraftFromContext = (context, kind) => {
  if (!context) return null;
  const params = {
    // useContentPlan removed — backend defaults to true; the content
    // plan is always used as context across all generation surfaces.
    inputSummary: `Use ${context.label} as reference context`,
  };
  if (context.assetId) params.referenceAssetId = context.assetId;
  if (context.publicUrl) params.publicUrl = context.publicUrl;
  if (kind === 'week' || kind === 'month') {
    const monthly = kind === 'month';
    return {
      text: `Create a ${monthly ? 'month' : 'week'} of posts from ${context.label}`,
      metadata: {
        actionId: `reference_${kind}`,
        type: 'content.generate_paid_batch',
        label: monthly ? 'Month from reference' : 'Week from reference',
        params: { ...params, planLengthDays: monthly ? 30 : 7, count: monthly ? 30 : 7, platform: 'instagram' },
      },
    };
  }
  return {
    text: `Create one post from ${context.label}`,
    metadata: {
      actionId: 'reference_post',
      type: 'content.create_post_from_reference',
      label: 'Post from reference',
      params,
    },
  };
};

function AssistantSocialAccountsCard({ payload, result, onNavigate }) {
  if (!isSocialAccountsPayload(payload)) return null;
  const connected = Array.isArray(payload.connectedPlatforms) ? payload.connectedPlatforms : [];
  const attention = Array.isArray(payload.needsAttentionPlatforms) ? payload.needsAttentionPlatforms : [];
  const missing = Array.isArray(payload.notConnectedPlatforms) ? payload.notConnectedPlatforms : [];
  const totalConnected = connected.reduce((sum, item) => sum + Number(item.connectedAccountCount || item.accountCount || 0), 0);

  const renderPlatform = (item, mode) => {
    const platform = item.platform;
    const accounts = Array.isArray(item.accounts) ? item.accounts : [];
    const connectAction = item.connectAction;
    const reconnectAction = item.reconnectAction;
    const primaryAction = mode === 'attention' ? reconnectAction : connectAction;
    const redirectUrl = primaryAction?.redirectUrl;
    const unavailable = primaryAction?.unavailable || (!item.connectable && item.reason ? { reason: item.reason } : null);
    const primaryActionLabel = mode === 'attention'
      ? i18n.t('assistant.social.reconnect', 'Reconnect')
      : mode === 'connected' && accounts.length > 0
        ? i18n.t('assistant.social.addAnother', 'Add another')
        : primaryAction?.label || i18n.t('assistant.social.connect', 'Connect');
    return (
      <div className="assistant-social-row" key={`${mode}-${platform}`}>
        <div className="assistant-social-row-main">
          <Platform name={platform} size={24} />
          <div className="assistant-social-text">
            <div className="assistant-social-platform">{socialPlatformLabel(platform)} · {accounts.length || 0}</div>
            {/* One status line per platform. Connected → account names;
                unavailable → the reason ONCE; Telegram → its setup hint;
                a plain connectable+empty platform shows nothing here (the
                section title + Connect button already say "not connected"). */}
            {accounts.length > 0 ? (
              <div className="assistant-social-copy">
                {accounts.map(account => account.displayName || account.externalAccountId || socialPlatformLabel(platform)).join(', ')}
              </div>
            ) : unavailable ? (
              <div className="assistant-social-warning">{unavailable.reason || i18n.t('assistant.social.backendUnavailable', 'Not available yet.')}</div>
            ) : primaryAction?.requiresExecutionForLinkToken ? (
              <div className="assistant-social-copy">{i18n.t('assistant.social.telegramSetupHint', 'Ask me to connect Telegram and I’ll create a secure setup link.')}</div>
            ) : null}
          </div>
        </div>
        <div className="assistant-social-actions">
          {redirectUrl && (
            <button className="assistant-result-link" onClick={() => openResultUrl(redirectUrl, onNavigate, { action: primaryAction, result, platform })}>
              {primaryActionLabel}
            </button>
          )}
          {mode === 'attention' && connectAction?.redirectUrl && (
            <button className="assistant-secondary-result-link assistant-result-link" onClick={() => openResultUrl(connectAction.redirectUrl, onNavigate, { action: connectAction, result, platform })}>
              {i18n.t('assistant.social.addAnother', 'Add another')}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="assistant-social-card">
      <div className="assistant-social-head">
        <div className="assistant-preview-title">{i18n.t('assistant.social.title', 'Social accounts')}</div>
        <span>{i18n.t('assistant.social.connectedCount', '{{count}} connected', { count: totalConnected })}</span>
      </div>
      {connected.length > 0 && <div className="assistant-social-section"><div className="assistant-social-section-title">{i18n.t('assistant.social.sectionConnected', 'Connected')}</div>{connected.map(item => renderPlatform(item, 'connected'))}</div>}
      {attention.length > 0 && <div className="assistant-social-section"><div className="assistant-social-section-title">{i18n.t('assistant.social.sectionAttention', 'Needs attention')}</div>{attention.map(item => renderPlatform(item, 'attention'))}</div>}
      {missing.length > 0 && <div className="assistant-social-section"><div className="assistant-social-section-title">{i18n.t('assistant.social.sectionNotConnected', 'Not connected')}</div>{missing.map(item => renderPlatform(item, 'missing'))}</div>}
    </div>
  );
}

function AssistantPreviewCard({ preview, action, onNavigate }) {
  const payload = previewPayload(preview);
  if (!payload) return null;
  if (isSocialAccountsPayload(payload)) return <AssistantSocialAccountsCard payload={payload} action={action} result={preview} onNavigate={onNavigate} />;
  const title = payload.type === 'week_plan_from_reference'
    ? i18n.t('assistant.preview.weeklyReferencePlan', 'Weekly reference plan')
    : payload.type === 'post_from_reference'
      ? i18n.t('assistant.preview.referenceDraftPreview', 'Reference draft preview')
      : action?.params?.platform ? i18n.t('assistant.preview.platformDraftPreview', '{{platform}} draft preview', { platform: platformDisplayName(action.params.platform) }) : i18n.t('assistant.preview.assistantPreview', 'Assistant preview');
  const asset = payload.asset || (payload.id && payload.type ? payload : null);
  const analysis = payload.analysis || payload.profileUpdate || payload.appliedGuidance || asset?.analysis || {};
  const fields = [
    [i18n.t('assistant.preview.fieldReference', 'Reference'), asset?.originalFilename || asset?.publicUrl || (asset?.id || payload.referenceAssetId ? i18n.t('assistant.preview.selectedReference', 'Selected reference') : null)],
    [i18n.t('assistant.preview.fieldReferenceType', 'Reference type'), asset?.type || payload.sourceType],
    [i18n.t('assistant.preview.fieldTarget', 'Target'), payload.targetPlatform || payload.platform],
    [i18n.t('assistant.preview.fieldHook', 'Hook'), payload.hook || payload.title || payload.headline],
    [i18n.t('assistant.preview.fieldAdaptedText', 'Adapted text'), payload.adaptedText || payload.caption || payload.body],
    [i18n.t('assistant.preview.fieldCta', 'CTA'), payload.cta],
    [i18n.t('assistant.preview.fieldHashtags', 'Hashtags'), Array.isArray(payload.hashtags) ? payload.hashtags.join(' ') : payload.hashtags],
    [i18n.t('assistant.preview.fieldVisualStyle', 'Visual style'), analysis.visualStyle],
    [i18n.t('assistant.preview.fieldColors', 'Colors'), Array.isArray(analysis.colors) ? analysis.colors.join(', ') : analysis.colors],
    [i18n.t('assistant.preview.fieldMood', 'Mood'), analysis.mood],
    [i18n.t('assistant.preview.fieldComposition', 'Composition'), analysis.composition],
    [i18n.t('assistant.preview.fieldConnection', 'Connection'), payload.targetConnection?.note],
  ].filter(([, value]) => value);
  const draftItems = Array.isArray(payload.drafts) ? payload.drafts.slice(0, 6) : [];
  const unavailable = payload.unavailable || preview?.result?.unavailable;
  return (
    <div className="assistant-preview-card">
      <div className="assistant-preview-title">{title}</div>
      {fields.length ? fields.map(([label, value]) => (
        <div className="assistant-preview-field" key={label}>
          <span>{label}</span>
          <p>{String(value)}</p>
        </div>
      )) : <div className="assistant-preview-empty">{i18n.t('assistant.ui.previewReady', 'Preview is ready. Confirm only if this is the result you want.')}</div>}
      {draftItems.length > 0 && (
        <div className="assistant-draft-list">
          {draftItems.map((draft, index) => (
            <div className="assistant-draft-row" key={`${draft.title || draft.hook || index}`}>
              <span>{draft.angle || i18n.t('assistant.result.draftN', 'Draft {{n}}', { n: index + 1 })}</span>
              <p>{draft.hook || draft.title || draft.caption}</p>
            </div>
          ))}
        </div>
      )}
      {unavailable && <div className="assistant-unavailable">{i18n.t('assistant.preview.unavailablePrefix', 'Unavailable:')} {unavailable.reason || i18n.t('assistant.preview.backendNotImplemented', 'Backend capability is not implemented.')}</div>}
    </div>
  );
}

const assistantResultFacts = (payload = {}, result = null, language = 'en') => {
  const tt = i18n.getFixedT(assistantLanguage(language));
  const facts = [];
  const draftCount = createdDraftCount(payload);
  if (draftCount > 0) facts.push(tt('assistant.result.savedToLibrary', '{{count}} saved to your Library', { count: draftCount }));
  if (payload.asset?.id) facts.push(payload.asset.analysisStatus ? tt('assistant.result.referenceSavedGuidance', 'Reference saved with reusable guidance') : tt('assistant.result.referenceSavedFuture', 'Reference saved for future use'));
  if (payload.brandProfileId) facts.push(tt('assistant.result.brandProfileUpdated', 'Brand Profile guidance updated'));
  if (payload.platformStatus?.reason) facts.push(payload.platformStatus.reason);
  return facts;
};

function AssistantResultCard({ result, action, onNavigate, language = 'en' }) {
  const payload = result?.result || {};
  const resultLanguage = assistantResultLanguage(result, language);
  const tt = i18n.getFixedT(assistantLanguage(resultLanguage));
  const view = actionResultView(action, result, resultLanguage);
  if (createdDraftCount(payload) > 0) {
    return <AssistantDraftResultCard result={result} action={action} payload={payload} onNavigate={onNavigate} language={resultLanguage} />;
  }
  const facts = assistantResultFacts(payload, result, resultLanguage);
  const receipt = assistantCreditReceipt(payload, result, resultLanguage);
  if (!result && !view) return null;
  return (
    <div className="assistant-result-card">
      <div className="assistant-result-text">{view?.message || result?.message || tt('assistant.result.backendReturned', 'Backend returned a result.')}</div>
      {receipt && <div className="assistant-credit-receipt"><Icon name="credit" size={13}/>{receipt}</div>}
      {facts.map((fact, index) => <div className="assistant-result-meta" key={`${fact}-${index}`}>{fact}</div>)}
      {Array.isArray(payload.drafts) && payload.drafts.length > 0 && (
        <div className="assistant-draft-list">
          {payload.drafts.slice(0, 5).map((draft, index) => (
            <div className="assistant-draft-row" key={`${draft.title || draft.hook || index}`}>
              <strong>{draft.title || draft.hook || tt('assistant.result.draftN', 'Draft {{n}}', { n: index + 1 })}</strong>
              <span>{draft.platform || tt('assistant.result.platformWord', 'platform')} - {draft.format || tt('assistant.result.postWord', 'post')}</span>
            </div>
          ))}
        </div>
      )}
      {isSocialAccountsPayload(payload) && <AssistantSocialAccountsCard payload={payload} action={action} result={result} onNavigate={onNavigate} />}
      {payload.unavailable && <div className="assistant-unavailable">{tt('assistant.preview.unavailablePrefix', 'Unavailable:')} {payload.unavailable.reason || tt('assistant.result.notImplemented', 'Not implemented')}</div>}
      {/* For insufficient_credits, render exactly ONE upgrade CTA via the
          view.label button below. The previous dual buttons (View plans +
          Choose plan) duplicated the same destination and looked cluttered.
          When extra-usage top-ups land they'll surface as a secondary
          action inside settings/billing — not as a second button here. */}
      {view?.label && (view.redirectUrl || view.screen) && (
        <button className="assistant-result-link" onClick={() => view.redirectUrl ? openResultUrl(view.redirectUrl, onNavigate, { action, result, platform: payload.platform }) : onNavigate?.(view.screen)}>{view.label}</button>
      )}
    </div>
  );
}

function AssistantDraftResultCard({ result, action, payload, onNavigate, language }) {
  const tt = i18n.getFixedT(assistantLanguage(language));
  const { content, refreshContent, startGenerationBatch } = useContentFeed();
  const [batchSnapshot, setBatchSnapshot] = useState(null);
  const drafts = createdDraftItems(payload);
  const primaryDraft = drafts[0] || {};
  const batchId = payload.batchId || generationBatchIdFromAssistantResult(result);
  const livePosts = React.useMemo(() => batchId
    ? content.filter(item => item.generationBatchId === batchId).map(item => normalizePost(item.raw || item)).filter(Boolean)
    : [], [content, batchId]);
  const firstLivePost = livePosts[0] || null;
  const draftCount = Math.max(livePosts.length, drafts.length, createdDraftCount(payload));
  const platforms = Array.isArray(payload.platforms) && payload.platforms.length
    ? payload.platforms
    : livePosts.flatMap(post => Object.keys(post.platformVersions || {})).concat(drafts.map(draft => draft.platform || draft.content?.platform || draft.draft?.targetPlatform || draft.draft?.platform)).filter(Boolean);
  const isGenerationSession = Boolean(batchId || String(payload.redirectUrl || '').includes('batch='));
  // Prefer the first short sentence of the backend's localized action message
  // over the legacy hardcoded "Generation started" copy — that way the card
  // title speaks the user's language and matches whatever the backend AI
  // actually wrote ("Опублікую перший одразу", "Publishing..."), instead of
  // showing a stale English string when the response is in Polish/Russian/etc.
  // Falls back to the hardcoded copy only when the backend message is missing
  // or too long to fit as a title.
  const fallbackTitle = isGenerationSession ? assistantCopy(language, 'generationStarted') : draftCount > 1 ? `${draftCount} ${assistantCopy(language, 'draftsCreated')}` : assistantCopy(language, 'created');
  const title = firstShortSentence(payload?.shortStatus) || firstShortSentence(payload?.message) || fallbackTitle;
  const preview = firstLivePost?.caption || firstLivePost?.body || primaryDraft.previewText || primaryDraft.content?.previewText || primaryDraft.caption || primaryDraft.draft?.caption || payload.previewText || payload.caption || result?.message;
  const platform = primaryDraft.platform || payload.platform || payload.content?.platform || payload.draft?.targetPlatform || payload.draft?.platform || action?.params?.platform;
  const mediaStatus = primaryDraft.mediaReuseStatus || primaryDraft.mediaSummary || payload.mediaReuseStatus || payload.mediaSummary || payload.draft?.mediaReuseStatus || {};
  const thumbnailUrl = firstUsableImageUrl(
    ...livePosts.map(post => post?.mediaAssets?.map(asset => asset?.publicUrl)),
    ...livePosts.map(post => post?.slides?.map(slide => slide?.imageUrl)),
    mediaStatus.imageUrl,
    mediaStatus.thumbnailUrl,
    mediaStatus.mediaUrls,
    ...drafts.map(draft => draft?.mediaReuseStatus?.imageUrl),
    ...drafts.map(draft => draft?.mediaReuseStatus?.mediaUrls),
    payload.mediaSummary?.thumbnailUrl,
    payload.draft?.mediaUrls,
    payload.mediaUrls
  );
  // For batch generation results we want the primary CTA to land on the
  // batch progress screen (/app/batch/:id) where the user sees every
  // language/platform tile, NOT a single post preview. The legacy logic
  // tried draft-level URLs first which routed to the first content_item's
  // /content/:id/preview and looked like "only one post". Batch-level
  // payload.redirectUrl (set by the backend to /app/batch/:id) now wins
  // when this is a generation session; single-draft results fall through
  // to the draft-level URLs as before.
  const openUrl = isGenerationSession
    ? (payload.redirectUrl || payload.openUrl || payload.previewUrl || primaryDraft.openUrl || primaryDraft.previewUrl || primaryDraft.redirectUrl || (payload.createdContentId ? `/app/post/${encodeURIComponent(payload.createdContentId)}` : null))
    : (primaryDraft.openUrl || primaryDraft.previewUrl || primaryDraft.redirectUrl || payload.openUrl || payload.previewUrl || payload.redirectUrl || (payload.createdContentId ? `/app/post/${encodeURIComponent(payload.createdContentId)}` : null));
  const draftTitle = compactDraftTitle(firstLivePost?.title)
    || compactDraftTitle(firstLivePost?.hook)
    || compactDraftTitle(firstLivePost?.caption || firstLivePost?.body)
    || compactDraftTitle(primaryDraft.title)
    || compactDraftTitle(payload.title || payload.content?.title || payload.draft?.title)
    || action?.title
    || title;
  const platformText = platformListLabel(platforms.length ? platforms : [platform].filter(Boolean));
  const batchStatus = assistantBatchStatus(batchSnapshot?.batch, livePosts, language);
  const thumbnailGrid = livePosts.map(post => firstUsableImageUrl(post.mediaAssets?.map(asset => asset?.publicUrl), post.slides?.map(slide => slide?.imageUrl))).filter(Boolean);

  useEffect(() => {
    if (!batchId) return undefined;
    let stopped = false;
    let intervalId = null;
    const poll = async () => {
      try {
        const batchResult = await api.content.getBatch(batchId);
        if (stopped) return;
        setBatchSnapshot(batchResult || null);
        if (batchResult?.batch) startGenerationBatch?.(batchResult.batch, []);
        await refreshContent?.({ background: true, refreshMetrics: false });
        if (assistantBatchTerminal(batchResult?.batch, livePosts) && intervalId) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      } catch {
        // Result cards are best-effort; the batch preview remains the source of truth.
      }
    };
    poll();
    intervalId = window.setInterval(poll, 4000);
    return () => {
      stopped = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [batchId]);

  const quotaNotices = Array.isArray(payload?.quotaNotices) ? payload.quotaNotices : [];
  const upgradeHref = typeof payload?.upgradeUrl === 'string' ? payload.upgradeUrl : null;

  return (
    <div className="assistant-result-card assistant-draft-result-card">
      <div className="assistant-draft-result-head">
        <div className="assistant-result-status"><Icon name="checkCircle" size={16}/>{title}</div>
        {isGenerationSession && <span className={'assistant-generation-status status-' + batchStatus.state}>{batchStatus.label}</span>}
      </div>
      {quotaNotices.length > 0 && (
        <div className="assistant-quota-notices">
          {quotaNotices.map((notice, index) => (
            <div
              key={`${notice?.metric || 'quota'}-${index}`}
              className={'assistant-quota-banner ' + (notice?.kind === 'downgrade' ? 'is-downgrade' : '')}
            >
              <span className="assistant-quota-icon">
                <Icon name={notice?.kind === 'downgrade' ? 'image' : 'alert'} size={13} />
              </span>
              <div className="assistant-quota-body">{notice?.message || tt('assistant.result.planLimitReached', 'Plan limit reached.')}</div>
              {upgradeHref && (
                <button
                  type="button"
                  className="assistant-quota-cta"
                  onClick={() => openResultUrl(upgradeHref, onNavigate, { action, result })}
                >
                  {tt('assistant.result.upgrade', 'Upgrade')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="assistant-draft-preview-card">
        {thumbnailUrl ? (
          <img className="assistant-draft-thumb" src={thumbnailUrl} alt="" loading="lazy" decoding="async" />
        ) : (
          <div className="assistant-draft-thumb is-empty"><Icon name="image" size={18}/></div>
        )}
        <div className="assistant-draft-copy">
          <div className="assistant-draft-title">{draftTitle}</div>
          {platformText && <div className="assistant-draft-platform-chip">{platformText}</div>}
          <div className="assistant-draft-preview">{preview}</div>
        </div>
      </div>
      {isGenerationSession && thumbnailGrid.length > 1 && (
        <div className="assistant-generation-thumbs">
          {thumbnailGrid.slice(0, 3).map((url, index) => <img key={`${url}-${index}`} src={url} alt="" loading="lazy" decoding="async" />)}
          {draftCount > 3 && <span>+{draftCount - 3}</span>}
        </div>
      )}
      {/* Per-draft list. Show only when there's something useful to display
          — i.e. drafts that already have real content (not the placeholder
          title "Generating: …"). While the batch is still generating we
          collapse the list so users don't see redundant placeholder rows
          ("Draft 1", "Draft 2"). The batch progress pill at the top of the
          card already communicates that generation is in flight. */}
      {drafts.length > 1 && drafts.some((d) => d.caption || d.body || d.previewText || (d.title && !/^Generating[:\s]/i.test(String(d.title)))) && (
        <div className="assistant-draft-result-list">
          {drafts.slice(0, 6).map((draft, index) => {
            const draftUrl = draft.openUrl || draft.previewUrl || draft.redirectUrl || (draft.contentId ? `/app/post/${encodeURIComponent(draft.contentId)}` : null);
            const body = draft.previewText || draft.caption || draft.body;
            return (
              <button
                type="button"
                className="assistant-draft-result-item"
                key={draft.contentId || `${draft.platform}-${index}`}
                onClick={() => draftUrl && openResultUrl(draftUrl, onNavigate, { action, result, platform: draft.platform })}
                disabled={!draftUrl}
              >
                <span>{socialPlatformLabel(draft.platform) || tt('assistant.result.variantN', 'Variant {{n}}', { n: index + 1 })}</span>
                <p>{body || assistantCopy(language, 'openDraft')}</p>
              </button>
            );
          })}
        </div>
      )}
      {/* Single primary action: open the batch progress screen (or live
          post preview if there's only one). "Change schedule" was redundant
          since the same target screen lets the user reschedule directly. */}
      <div className="assistant-result-actions">
        {openUrl && (
          <button
            className="assistant-result-link assistant-primary-result-link"
            onClick={() => openResultUrl(openUrl, onNavigate, { action, result, platform })}
          >
            {assistantCopy(language, isGenerationSession ? 'openGeneration' : 'openDraft')}
          </button>
        )}
      </div>
    </div>
  );
}

function assistantBatchStatus(batch, posts, language = 'en') {
  const tt = i18n.getFixedT(assistantLanguage(language));
  if (posts.some(post => post.status === 'failed') || batch?.status === 'failed' || batch?.status === 'partial_failed') return { state: 'failed', label: tt('assistant.result.statusNeedsRetry', 'Needs retry') };
  if (!posts.length || posts.some(post => post.status === 'generating')) return { state: 'generating', label: tt('assistant.result.statusGenerating', 'Generating') };
  if (posts.some(post => post.generationStage === 'visualizing')) return { state: 'visualizing', label: tt('assistant.result.statusVisualizing', 'Visualizing') };
  if (posts.some(post => post.status === 'scheduled') || batch?.scheduleMode === 'schedule') return { state: 'scheduled', label: tt('assistant.result.statusBestTimesSet', 'Best times set') };
  if (posts.length && posts.every(post => ['ready', 'published', 'scheduled'].includes(post.status))) return { state: 'ready', label: tt('assistant.result.statusReady', 'Ready') };
  if (posts.some(post => post.mediaAssets?.length)) return { state: 'visualizing', label: tt('assistant.result.statusVisualsReady', 'Visuals ready') };
  return { state: 'generating', label: tt('assistant.result.statusGenerating', 'Generating') };
}

function assistantBatchTerminal(batch, posts) {
  if (batch && !['queued', 'generating', 'reserved'].includes(batch.status)) return true;
  return posts.length > 0 && posts.every(post => ['ready', 'scheduled', 'published', 'failed'].includes(post.status));
}

const friendlyAssistantError = (error) => {
  if (error?.status === 401) return i18n.t('assistant.ui.errorAuth', 'Please sign in again before using Ask AI.');
  // fix #5 (judge review): a QUOTA_EXCEEDED-coded branch here was removed —
  // the assistant-service never emits that code for a 0 limit (it answers
  // 200 with a canned message instead), and this same helper also serves
  // previewAction/confirmAction/uploadAttachment, where a Growth user's REAL
  // QUOTA_EXCEEDED (e.g. a different quota entirely) would have been
  // mislabeled "available from Starter" by this branch. The proactive lock
  // in AIPanel (see the `locked`/`allowance` render below) is what actually
  // keeps a 0-limit plan out of this code path; this helper only needs to
  // stay generic.
  //
  // Card gate (past_due / no active subscription): saying "could not reach the
  // backend" for a deliberate 402 reads as an outage — tell the truth instead.
  // TASK-331: PLAN_LAPSED first — a lapsed org's 402 here must not say
  // "Activate ... your free trial"-flavored copy; reuse the lapsed banner's
  // own concise line instead of introducing a new untranslated key.
  if (error?.code === 'PLAN_LAPSED') return i18n.t('paymentIssue.lapsedBody');
  if (error?.status === 402) return i18n.t('assistant.ui.errorBilling', 'Ask AI needs an active plan. Activate (or fix your payment) in Settings → Billing and your message will go through.');
  if (error?.status === 429) return error.message || i18n.t('assistant.ui.errorRate', 'Ask AI is rate limited right now. Try again shortly.');
  return i18n.t('assistant.ui.errorNetwork', 'Ask AI could not reach the assistant backend. Your message is still here, and you can retry.');
};

function AssistantActionCard({ action, response, selectedEntity, state, onPreview, onConfirm, onCancel, onNavigate, creationAllowed, language = 'en' }) {
  void selectedEntity;
  void onPreview;
  const usage = assistantUsageText(response, action);
  const needsConfirmation = actionNeedsConfirmation(response, action);
  const canConfirm = Boolean(action?.id);
  const draftAction = { ...action, params: action?.params || {} };
  const isBusy = state?.status === 'confirming';
  const isConfirmed = state?.status === 'confirmed';
  const isCancelled = state?.status === 'cancelled';
  const isError = state?.status === 'error';
  const result = state?.result;
  const preview = state?.preview;
  const primaryLabel = needsConfirmation ? i18n.t('assistant.ui.confirm', 'Confirm') : (action?.ui?.primaryLabel || i18n.t('assistant.ui.run', 'Run'));
  const socialPlatform = action?.type?.startsWith('social.') ? actionPlatform(action) : null;

  return (
    <div className="assistant-action-card assistant-action-card-compact">
      {!isConfirmed && !isCancelled && (
        <>
          {socialPlatform ? (
            <div className="assistant-action-social-head">
              <Platform name={socialPlatform} size={28} />
              <div className="assistant-action-summary">{actionSummary(action)}</div>
            </div>
          ) : (
            <div className="assistant-action-summary">{actionSummary(action)}</div>
          )}
          {usage && <div className="assistant-action-cost">{i18n.getFixedT(assistantLanguage(language))('assistant.ui.uses', 'Uses')} {usage}</div>}
        </>
      )}
      {!isConfirmed && !isCancelled && (
        <div className="assistant-confirm-actions">
          <button className="assistant-confirm-btn" onClick={() => onConfirm(draftAction)} disabled={isBusy || !canConfirm || !creationAllowed}>
            {isBusy ? i18n.t('assistant.ui.working', 'Working...') : primaryLabel}
          </button>
          <button className="assistant-cancel-btn" onClick={() => onCancel(draftAction)} disabled={isBusy}>
            {action?.ui?.secondaryLabel || i18n.t('assistant.ui.cancel', 'Cancel')}
          </button>
          {!canConfirm && <div className="assistant-action-error">{i18n.t('assistant.ui.missingActionId', 'Backend action ID is missing. Ask the assistant to prepare it again.')}</div>}
        </div>
      )}
      {preview && <AssistantPreviewCard preview={preview} action={draftAction} onNavigate={onNavigate} />}
      {isConfirmed && <AssistantResultCard result={result} action={draftAction} onNavigate={onNavigate} language={language} />}
      {isCancelled && <div className="assistant-cancelled">{i18n.t('assistant.ui.cancelledNoChanges', 'Cancelled. No changes made.')}</div>}
      {isError && <div className="assistant-action-error">{state.message || i18n.t('assistant.ui.confirmFailed', 'Confirmation failed. No changes were made.')}</div>}
    </div>
  );
}

function AssistantPlatformPicker({ group, onSelect }) {
  const [selected, setSelected] = useState([]);
  if (!group?.options?.length) return null;

  // option.id may be a compound key like "telegram:soc_xxx" for per-account
  // rows; the underlying platform always lives in option.params.platforms[0].
  const platformOfOption = (option) => option?.params?.platforms?.[0] || option?.id || null;

  const optionIds = group.options.map(option => option.id);
  const selectedOptions = group.options.filter(option => selected.includes(option.id));
  const submitSelection = (ids) => {
    const picked = group.options.filter(option => ids.includes(option.id));
    if (!picked.length) return;
    const labels = picked.map(option => option.label || option.prompt).filter(Boolean);
    // Merge platforms (deduped) and targetAccountIds across selected rows so
    // the user can mix per-account picks (e.g. Telegram channel A + Telegram
    // channel B, or Telegram channel A + Instagram).
    const platforms = Array.from(new Set(picked.flatMap(option => option.params?.platforms || [option.id]).filter(Boolean)));
    const targetAccountIds = Array.from(new Set(picked.flatMap(option => option.params?.targetAccountIds || []).filter(Boolean)));
    const params = { platforms };
    if (targetAccountIds.length) params.targetAccountIds = targetAccountIds;
    onSelect?.({
      label: labels.join(', '),
      prompt: labels.join(', '),
      kind: 'platform',
      params,
    });
  };
  const toggleOption = (option) => {
    if (!group.multiple) {
      submitSelection([option.id]);
      return;
    }
    setSelected(current => current.includes(option.id)
      ? current.filter(id => id !== option.id)
      : [...current, option.id]);
  };

  return (
    <div className="assistant-platform-picker">
      <div className="assistant-platform-picker-title">{group.title || i18n.t('assistant.ui.pickChannels', 'Pick channels')}</div>
      <div className="assistant-platform-options" role="group" aria-label={group.title || i18n.t('assistant.ui.pickChannels', 'Pick channels')}>
        {group.options.map(option => {
          const active = selected.includes(option.id);
          const platform = platformOfOption(option);
          return (
            <button
              key={option.id}
              type="button"
              className={'assistant-platform-option ' + (active ? 'is-active' : '')}
              aria-pressed={active}
              onClick={() => toggleOption(option)}
            >
              <Platform name={platform} size={18} />
              {option.label || option.prompt}
            </button>
          );
        })}
      </div>
      {group.multiple && (
        <div className="assistant-platform-picker-actions">
          <button type="button" className="assistant-platform-submit" disabled={!selectedOptions.length} onClick={() => submitSelection(selected)}>
            {group.submitLabel || i18n.t('assistant.ui.useSelected', 'Use selected')}
          </button>
          <button type="button" className="assistant-platform-all" onClick={() => submitSelection(optionIds)}>
            {group.allLabel || i18n.t('assistant.ui.allShown', 'All shown')}
          </button>
        </div>
      )}
    </div>
  );
}

// Tolerant extraction of a compact source-post preview from api.content.get —
// response shapes vary (contentItem/activeVersion vs flattened raw).
function extractSourcePost(resp, contentId) {
  const data = resp?.data ?? resp;
  if (!data || typeof data !== 'object') return null;
  const item = data.contentItem || data.item || data;
  const version = data.activeVersion || item.activeVersion || null;
  const text = version?.body || version?.caption || version?.hook || item.title || item.topic || '';
  const image = (Array.isArray(data.mediaUrls) && data.mediaUrls[0])
    || data.mediaPreview?.url
    || version?.imageUrl
    || null;
  if (!text && !image) return null;
  return { id: contentId, text: String(text).slice(0, 140), image };
}

// Compact "which post is this about" card — rendered when the assistant is
// opened from a recommendation, clickable through to the full post.
function SourcePostCard({ post }) {
  const navigate = useNavigate();
  if (!post?.id) return null;
  const open = () => navigate(`/app/post/${encodeURIComponent(post.id)}`);
  return (
    <button type="button" className="as-source-card" title={i18n.t('bell.viewPost')} onClick={open}>
      {post.image && <span className="as-source-img" style={{ backgroundImage: `url("${post.image}")` }} aria-hidden />}
      <span className="as-source-body">
        <span className="as-source-l">{i18n.t('bell.sourcePost')}</span>
        {post.text && <span className="as-source-t">{post.text}</span>}
      </span>
      <Icon name="chev_r" size={13} />
    </button>
  );
}

function AssistantBubble({ message, onRetry, onCancel, onPreviewAction, onConfirmAction, onQuickReply, selectedEntity, onNavigate, creationAllowed, variant = 'legacy' }) {
  if (message.kind === 'sourcePost') {
    return <SourcePostCard post={message.post} />;
  }
  const actions = Array.isArray(message.response?.proposedActions) ? message.response.proposedActions : [];
  const quickReplies = assistantQuickReplies(message.response);
  const quickReplyGroup = assistantQuickReplyGroup(message.response);
  const usage = actions.length ? null : assistantUsageText(message.response);
  const language = assistantLanguage(message.response?.responseLanguage);
  const isAi = message.from === 'ai';
  const canStream = variant === 'panel' && isAi && !message.persisted && !message.error && Boolean(message.text);
  const baseClass = variant === 'panel'
    ? `as-bubble ${isAi ? 'as-bubble-bot' : 'as-bubble-user'}${message.error ? ' is-error' : ''}`
    : `chatbar-bubble ${isAi ? 'is-ai' : 'is-u'}${message.error ? ' is-error' : ''}`;

  return (
    <div className={baseClass}>
      <div>{canStream ? <StreamedText text={message.text} enabled /> : message.text}</div>
      {usage && <div className="assistant-usage-pill">{Number(message.response?.creditCost || 0) > 0 ? `${i18n.getFixedT(assistantLanguage(language))('assistant.ui.quota', 'Quota:')} ${usage}` : usage}</div>}
      {message.error && message.retryText && (
        <button className="assistant-inline-btn" disabled={!creationAllowed} onClick={() => onRetry(message.retryText)}>
          {i18n.t('assistant.ui.retry', 'Retry')}
        </button>
      )}
      {message.resultCard && <AssistantResultCard result={message.resultCard} action={message.resultCard.action} onNavigate={onNavigate} language={language} />}
      {creationAllowed && quickReplyGroup && <AssistantPlatformPicker group={quickReplyGroup} onSelect={onQuickReply} />}
      {quickReplies.length > 0 && (
        <div className="assistant-quick-replies">
          {quickReplies.map((reply, index) => (
            <button
              type="button"
              className={'assistant-quick-reply ' + (reply.kind ? `is-${reply.kind}` : '')}
              key={`${reply.label || reply.prompt}-${index}`}
              onClick={() => onQuickReply?.(reply)}
              disabled={!creationAllowed}
            >
              {reply.label || reply.prompt}
            </button>
          ))}
        </div>
      )}
      {actions.map((action, index) => (
        <AssistantActionCard
          key={`${action.type}-${index}`}
          action={action}
          creationAllowed={creationAllowed}
          response={message.response}
          selectedEntity={selectedEntity}
          state={message.actionStates?.[index]}
          onPreview={(draftAction) => onPreviewAction(message.id, index, draftAction)}
          onConfirm={(draftAction) => onConfirmAction(message.id, index, draftAction)}
          onCancel={() => onCancel(message.id, index)}
          onNavigate={onNavigate}
          language={language}
        />
      ))}
      {message.response?.safeToExecute === false && actions.length === 0 && (
        <div className="assistant-cancelled">{i18n.t('assistant.ui.unsafeAction', 'Unsupported or unsafe action. No changes made.')}</div>
      )}
    </div>
  );
}

function AssistantAttachmentModal({ open, onClose, onUploadFiles }) {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const uploadingRef = useRef(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Thumbnails: object URLs live exactly as long as the selection does, so
  // repeated open/close cycles don't leak blobs. Creating them after commit
  // also avoids leaking URLs from abandoned StrictMode/concurrent renders.
  useEffect(() => {
    const nextPreviews = files.map(file => URL.createObjectURL(file));
    setPreviews(nextPreviews);
    return () => nextPreviews.forEach(url => URL.revokeObjectURL(url));
  }, [files]);

  // Reset transient selection only after the dialog has actually closed.
  useEffect(() => {
    if (!open) {
      setFiles(existing => (existing.length ? [] : existing));
      setError('');
      setDragging(false);
      uploadingRef.current = false;
      setUploading(false);
    }
  }, [open]);

  // Move keyboard focus into the modal, then restore it to the trigger when
  // the modal closes. This effect intentionally depends only on `open` so an
  // inline parent callback cannot repeatedly reset focus.
  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const frame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(frame);
      const previous = previousFocusRef.current;
      previousFocusRef.current = null;
      if (previous?.isConnected) previous.focus();
    };
  }, [open]);

  // Keep Tab navigation inside the dialog. Escape is deliberately ignored
  // while uploads are active so saved assets cannot continue in a hidden UI.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!uploadingRef.current) onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(ASSISTANT_MODAL_FOCUSABLE))
        .filter(element => element.getClientRects().length > 0);
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, uploading]);

  if (!open) return null;

  const addFiles = (value) => {
    if (uploadingRef.current) return;
    const incoming = Array.from(value || []);
    if (!incoming.length) return;
    const images = incoming.filter(file => /^image\//i.test(file.type || ''));
    const accepted = images.filter(isReferenceImageFile);
    if (!images.length) {
      setError(i18n.t('assistant.ui.chooseImagesOnly', 'Choose image files only.'));
    } else if (accepted.length < images.length) {
      setError(i18n.t('assistant.ui.imageTooLarge', 'Some images are over 8 MB and were skipped.'));
    } else {
      setError('');
    }
    if (accepted.length) {
      setFiles(existing => [...existing, ...accepted].slice(0, MAX_ASSISTANT_IMAGES));
    }
  };

  const removeFile = (index) => {
    if (uploadingRef.current) return;
    setFiles(existing => existing.filter((_, position) => position !== index));
    setError('');
  };

  const requestClose = () => {
    if (!uploadingRef.current) onCloseRef.current?.();
  };

  const uploadSelectedFiles = async () => {
    if (!files.length) {
      setError(i18n.t('assistant.ui.chooseAtLeastOne', 'Choose at least one image.'));
      return;
    }
    uploadingRef.current = true;
    setUploading(true);
    setError('');
    try {
      await onUploadFiles(files.slice(), 'Image added by user');
      setFiles([]);
      onCloseRef.current?.();
    } catch (uploadError) {
      setError(uploadError?.message || i18n.t('assistant.ui.uploadFailed', 'Upload failed. No image was saved.'));
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  return (
    <div className="assistant-attach-scrim" role="presentation" onMouseDown={requestClose}>
      <div
        ref={dialogRef}
        className="assistant-attach-modal as-attach"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-busy={uploading}
        tabIndex={-1}
        onMouseDown={event => event.stopPropagation()}
      >
        <div className="assistant-attach-head">
          <div>
            <div className="assistant-action-eyebrow">{i18n.t('assistant.ui.imagesEyebrow', 'Images')}</div>
            <h3 id={titleId}>{i18n.t('assistant.ui.addImages', 'Add images')}</h3>
            <p id={descriptionId}>{i18n.t('assistant.ui.attachPurpose', 'Saved to this brand as a reference photo. The assistant then builds posts around your image instead of inventing one — ask for a single post, a week, or a month.')}</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="assistant-attach-close"
            onClick={requestClose}
            disabled={uploading}
            aria-label={i18n.t('assistant.ui.closeAttachmentOptions', 'Close attachment options')}
          >
            <Icon name="x" size={16}/>
          </button>
        </div>

        <label
          className={'as-dz' + (dragging ? ' is-dragging' : '')}
          aria-disabled={uploading}
          onDrop={event => {
            event.preventDefault();
            setDragging(false);
            addFiles(event.dataTransfer?.files);
          }}
          onDragOver={event => event.preventDefault()}
          onDragEnter={event => { event.preventDefault(); setDragging(true); }}
          onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
        >
          <input
            className="as-dz-input"
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            multiple
            disabled={uploading}
            onChange={event => { addFiles(event.target.files); event.target.value = ''; }}
          />
          <span className="as-dz-icon"><Icon name="image" size={20}/></span>
          <span className="as-dz-title">{i18n.t('assistant.ui.dropImagesHere', 'Drop images here')}</span>
          <span className="as-dz-hint">{i18n.t('assistant.ui.imageFormatHelp', 'PNG, JPG, WebP, or GIF up to 8 MB each.')}</span>
          <span className="as-dz-browse">{i18n.t('assistant.ui.browseFiles', 'Browse files')}</span>
        </label>

        {files.length > 0 && (
          <div className="as-dz-thumbs">
            {files.map((file, index) => (
              <div className="as-thumb" key={`${file.name}-${file.size}-${index}`}>
                {previews[index]
                  ? <img className="as-thumb-img" src={previews[index]} alt={file.name}/>
                  : <span className="as-thumb-img" aria-hidden="true"/>}
                <button
                  type="button"
                  className="as-thumb-x"
                  onClick={() => removeFile(index)}
                  disabled={uploading}
                  aria-label={i18n.t('assistant.ui.removeImage', { filename: file.name, defaultValue: 'Remove {{filename}}' })}
                >
                  <Icon name="x" size={12}/>
                </button>
                <span className="as-thumb-name">{file.name}</span>
              </div>
            ))}
          </div>
        )}

        {error && <div className="assistant-action-error as-attach-error" role="alert" aria-live="assertive">{error}</div>}

        <div className="as-attach-foot">
          <span className="as-attach-count" aria-live="polite" aria-atomic="true">
            {files.length
              ? i18n.t('assistant.ui.selectedCount', { picked: files.length, max: MAX_ASSISTANT_IMAGES, defaultValue: '{{picked}} of {{max}} selected' })
              : ''}
          </span>
          <div className="as-attach-actions">
            <button type="button" className="assistant-cancel-btn" onClick={requestClose} disabled={uploading}>{i18n.t('assistant.ui.cancel', 'Cancel')}</button>
            <button type="button" className="assistant-confirm-btn" onClick={uploadSelectedFiles} disabled={uploading || !files.length}>{uploading ? i18n.t('assistant.ui.adding', 'Adding...') : i18n.t('assistant.ui.useImages', 'Use images')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssistantActionTray({ currentScreen, selectedEntity, open, onSelect }) {
  const locale = assistantActionLocale();
  const actions = assistantActionsForContext({ currentScreen, selectedEntity });

  if (!open || !actions.length) return null;

  return (
    <div className="assistant-action-tray assistant-action-menu">
      <div className="assistant-action-tray-head">
        <div>
          <strong>{assistantTrayText('tray.title', locale)}</strong>
          <span>{assistantTrayText('tray.subtitle', locale)}</span>
        </div>
      </div>
      <div className="assistant-action-options" role="listbox" aria-label={assistantTrayText('tray.title', locale)}>
        {actions.map(action => (
          <button
            key={action.id}
            type="button"
            className="assistant-action-option"
            onClick={() => onSelect?.(buildAssistantActionDraft(action))}
          >
            <span className="assistant-action-option-icon"><Icon name={action.icon || 'sparkles'} size={14}/></span>
            <span>
              <strong>{assistantActionText(action, 'label', locale)}</strong>
              <small>{assistantActionText(action, 'description', locale)}</small>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function AssistantReferenceChip({ context, onClear, onUse }) {
  if (!context) return null;
  return (
    <div className="assistant-reference-chip">
      <span className="assistant-reference-chip-main"><Icon name="image" size={11}/>{context.label}</span>
      <span className="assistant-reference-saved">{i18n.t('assistant.ui.imageAdded', 'Image added')}</span>
      <button type="button" className="assistant-reference-clear" onClick={onClear} aria-label={i18n.t('assistant.ui.clearReference', 'Clear reference')}><Icon name="x" size={10}/></button>
    </div>
  );
}

async function sendAssistantMessage({
  text,
  currentScreen,
  selectedEntity,
  actionMetadata = null,
  setMessages,
  setInput,
  setExpanded,
  setThinking,
  appendUser = true,
  onAssistantResult,
}) {
  const trimmed = text.trim();
  if (!trimmed) return;

  if (appendUser) {
    setMessages(messages => [...messages, { id: makeMessageId('user'), from: 'user', text: trimmed }]);
    setInput('');
  }
  setExpanded?.(true);
  setThinking(true);

  try {
    const response = await api.assistant.chat({
      message: trimmed,
      currentScreen,
      selectedEntity: selectedEntity || null,
      actionMetadata,
    });
    setMessages(messages => [...messages, {
      id: makeMessageId('ai'),
      from: 'ai',
      text: response?.message || i18n.t('assistant.ui.emptyResponse', 'I received your message, but the assistant returned an empty response.'),
      response,
      resultCard: response?.resultCard || null,
    }]);
    notifyAssistantResult(onAssistantResult, response);
  } catch (error) {
    setMessages(messages => [...messages, {
      id: makeMessageId('error'),
      from: 'ai',
      text: friendlyAssistantError(error),
      error: true,
      retryText: trimmed,
    }]);
  } finally {
    setThinking(false);
  }
}

function notifyAssistantResult(callback, result) {
  if (typeof callback !== 'function') return;
  Promise.resolve(callback(result)).catch(() => {});
}

function useStreamedText(text, { speedMs = 14, enabled = true } = {}) {
  const [shown, setShown] = useState(enabled ? '' : text);
  const [done, setDone] = useState(!enabled);
  useEffect(() => {
    if (!enabled) { setShown(text); setDone(true); return undefined; }
    setShown(''); setDone(false);
    if (!text) { setDone(true); return undefined; }
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(id);
        setDone(true);
      }
    }, speedMs);
    return () => clearInterval(id);
  }, [text, speedMs, enabled]);
  return { shown, done };
}

function StreamedText({ text, enabled }) {
  const { shown, done } = useStreamedText(text || '', { enabled });
  const lines = (shown || '').split('\n');
  return (
    <>
      {lines.map((line, i) => (
        <span key={i}>{line}{i < lines.length - 1 && <br/>}</span>
      ))}
      {!done && <span className="as-cursor"/>}
    </>
  );
}

function pendingActionMetadataForMessage(text, pendingActionMetadata, options = {}) {
  if (Object.prototype.hasOwnProperty.call(options, 'actionMetadata')) return options.actionMetadata;
  if (!pendingActionMetadata) return null;
  return shouldDropPendingActionMetadata(text, pendingActionMetadata) ? null : pendingActionMetadata;
}

function shouldDropPendingActionMetadata(text, metadata) {
  if (!metadata || !/https?:\/\//i.test(text)) return false;
  const normalized = text.toLowerCase();
  const asksForPost = /\b(post|draft|caption)\b|пост|допис/i.test(normalized);
  const asksForPlan = /\b(week|weekly|month|monthly|7|30)\b|тиж|місяц|місяч|семь|тридц/i.test(normalized);
  return asksForPost && !asksForPlan && metadata.type !== 'content.create_post_from_reference';
}

export const AIChatBar = ({ currentScreen, selectedEntity = null, expanded, setExpanded, onVoice, sidebarCollapsed }) => {
  const creationAllowed = creationAccessState(useDashboardData()) === 'allowed';
  const [messages, setMessages] = useState(() => [greetingMessage(currentScreen)]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [attachmentOpen, setAttachmentOpen] = useState(false);
  const [actionTrayOpen, setActionTrayOpen] = useState(false);
  const [pendingActionMetadata, setPendingActionMetadata] = useState(null);
  const [referenceContext, setReferenceContext] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // GenerationStatus integration: any confirmAction that kicks off a
  // long-running backend job (batch generation, plan regen, social
  // connect) registers via trackAssistantResult so the global plate
  // tracks it across navigation. Has a no-op fallback if provider not
  // mounted (e.g. legacy MainApp screens).
  const { startOperation } = useGenerationStatus();

  useEffect(() => {
    let cancelled = false;

    async function loadAssistantHistory() {
      try {
        const result = await api.assistant.messages({ limit: 50 });
        const history = Array.isArray(result?.messages)
          ? result.messages.map(persistedMessageToBubble).filter(message => message.text)
          : [];
        if (!cancelled) {
          setMessages(history.length ? history : [greetingMessage(currentScreen)]);
        }
      } catch {
        if (!cancelled) {
          setMessages([greetingMessage(currentScreen)]);
        }
      }
    }

    loadAssistantHistory();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setMessages(existing => hasConversationMessages(existing) ? existing : [greetingMessage(currentScreen)]);
  }, [currentScreen]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking, expanded]);

  const send = (txt, options = {}) => {
    if (!creationAllowed) return Promise.resolve(null);
    const trimmed = txt.trim();
    const actionMetadata = pendingActionMetadataForMessage(trimmed, pendingActionMetadata, options);
    setActionTrayOpen(false);
    if (trimmed) setPendingActionMetadata(null);
    return sendAssistantMessage({
      text: txt,
      currentScreen,
      selectedEntity,
      actionMetadata,
      setMessages,
      setInput,
      setExpanded,
      setThinking,
      appendUser: options.appendUser !== false,
    });
  };

  const sendQuickReply = (reply) => {
    const text = reply?.prompt || reply?.label || '';
    const actionMetadata = quickReplyActionMetadata(reply);
    return actionMetadata ? send(text, { actionMetadata }) : send(text);
  };

  const insertActionDraft = (draft) => {
    if (!draft?.metadata) return;
    setPendingActionMetadata(draft.metadata || null);
    setActionTrayOpen(false);
    setExpanded?.(true);
    window.requestAnimationFrame?.(() => inputRef.current?.focus());
  };

  const useReferenceContext = (kind) => {
    const draft = referenceActionDraftFromContext(referenceContext, kind);
    if (draft) insertActionDraft(draft);
  };

  const cancelConfirmation = (id) => {
    setMessages(messages => messages.map(message => message.id === id ? { ...message, cancelled: true } : message));
  };

  const setActionState = (messageId, actionIndex, state) => {
    setMessages(messages => messages.map(message => {
      if (message.id !== messageId) return message;
      const actionStates = { ...(message.actionStates || {}) };
      actionStates[actionIndex] = { ...(actionStates[actionIndex] || {}), ...state };
      return { ...message, actionStates };
    }));
  };

  const previewAction = async (messageId, actionIndex, action) => {
    setActionState(messageId, actionIndex, { status: 'previewing' });
    try {
      const preview = await api.assistant.previewAction({ action });
      setActionState(messageId, actionIndex, { status: 'previewed', preview });
    } catch (error) {
      setActionState(messageId, actionIndex, { status: 'error', message: friendlyAssistantError(error) });
    }
  };

  const confirmAction = async (messageId, actionIndex, action) => {
    if (!creationAllowed) return;
    setActionState(messageId, actionIndex, { status: 'confirming' });
    try {
      const result = await api.assistant.confirmAction({ action, confirmed: true });
      // Register the assistant result with the global status plate so
      // long-running ops (batch gen, plan regen, social connect) are
      // visible everywhere — not just inside the chat bubble's result
      // card. trackAssistantResult is a no-op if the payload isn't a
      // long-running operation.
      try {
        trackAssistantResult(startOperation, result, action?.title);
      } catch (_) { /* best-effort */ }
      if (result?.assistantResponse) {
        setActionState(messageId, actionIndex, { status: 'confirmed', result: null });
        setMessages(messages => [...messages, assistantResponseToBubble(result.assistantResponse, result.message)]);
        return;
      }
      setActionState(messageId, actionIndex, { status: 'confirmed', result });
    } catch (error) {
      setActionState(messageId, actionIndex, { status: 'error', message: friendlyAssistantError(error) });
    }
  };

  const cancelAction = (messageId, actionIndex) => {
    setActionState(messageId, actionIndex, { status: 'cancelled' });
  };

  const uploadAttachment = async (attachment) => {
    if (!creationAllowed) return null;
    setMessages(messages => [...messages, { id: makeMessageId('user'), from: 'user', text: i18n.t('assistant.ui.attachedFile', { filename: attachment.originalFilename || attachment.publicUrl || attachment.type || 'context', defaultValue: 'Attached {{filename}}' }) }]);
    try {
      const result = await api.assistant.uploadAttachment({ ...attachment, currentScreen, selectedEntity });
      const nextReferenceContext = assistantReferenceContextFromResult(attachment, result);
      if (nextReferenceContext) setReferenceContext(nextReferenceContext);
      setMessages(messages => [...messages, {
        id: makeMessageId('attachment-result'),
        from: 'ai',
        text: result?.message || i18n.t('assistant.ui.attachmentProcessed', 'Attachment processed by backend.'),
        resultCard: {
          executed: result?.status !== 'unavailable',
          message: result?.message,
          result,
          action: { type: 'reference.upload_asset', title: i18n.t('assistant.ui.referenceAttachment', 'Reference attachment') },
        },
      }]);
      const assetId = result?.asset?.id || result?.asset?.assetId;
      return assetId || null;
    } catch (error) {
      setMessages(messages => [...messages, { id: makeMessageId('attachment-error'), from: 'ai', text: friendlyAssistantError(error), error: true }]);
      return null;
    }
  };

  const uploadReferenceFiles = async (selectedFiles, notes = 'Image added by user') => {
    const followUp = input.trim();
    const assetIds = [];
    const acceptedFiles = Array.from(selectedFiles || [])
      .filter(isReferenceImageFile)
      .slice(0, MAX_ASSISTANT_IMAGES);

    for (const file of acceptedFiles) {
      try {
        const dataUrl = await fileToDataUrl(file);
        const assetId = await uploadAttachment({
          type: 'product_image',
          attachmentPurpose: 'product_image',
          dataUrl,
          originalFilename: file.name,
          mimeType: file.type || 'image/png',
          notes,
        });
        if (assetId) assetIds.push(assetId);
      } catch (error) {
        setMessages(messages => [...messages, { id: makeMessageId('attachment-error'), from: 'ai', text: friendlyAssistantError(error), error: true }]);
      }
    }

    if (!assetIds.length) {
      throw new Error(i18n.t('assistant.ui.uploadFailed', 'Upload failed. No image was saved.'));
    }

    if (followUp) {
      await sendAssistantMessage({
        text: referenceFollowUpPrompt(followUp, assetIds),
        currentScreen,
        selectedEntity: {
          type: 'referenceAsset',
          id: assetIds[0],
          ...(assetIds.length > 1 ? { inputSummary: `Reference assets: ${assetIds.join(', ')}` } : {}),
        },
        setMessages,
        setInput,
        setExpanded,
        setThinking,
        appendUser: true,
      });
    }

    return assetIds;
  };

  const uploadFileAsReference = async (file, notes = 'Dropped into Assistant') => {
    if (!isReferenceImageFile(file)) return;
    try {
      await uploadReferenceFiles([file], notes);
    } catch {
      // The upload helper already appends a visible chat error for this path.
    }
  };

  const handleInputPaste = (event) => {
    const file = Array.from(event.clipboardData?.files || []).find(isReferenceImageFile);
    if (file) {
      event.preventDefault();
      uploadFileAsReference(file, 'Pasted into Assistant');
    }
  };

  const handleReferenceDrop = (event) => {
    event.preventDefault();
    setExpanded?.(true);
    const file = Array.from(event.dataTransfer?.files || []).find(isReferenceImageFile);
    if (file) {
      uploadFileAsReference(file, 'Dropped into Assistant');
      return;
    }
    const url = extractFirstHttpUrl(event.dataTransfer?.getData('text/uri-list') || event.dataTransfer?.getData('text/plain') || '');
    if (url) setInput(existing => existing.trim() ? `${existing.trim()} ${url}` : url);
  };

  return (
    <div className={'chatbar ' + (expanded ? 'chatbar-expanded' : '')} data-collapsed={String(!!sidebarCollapsed)}>
      {expanded && (
        <div className="chatbar-msgs-wrap">
          <div className="chatbar-msgs-head">
            <div className="row" style={{gap:10}}>
              <div className="asst-orb-mini"/>
              <div>
                <div style={{fontWeight:600, fontSize:13}}>{ASSISTANT_NAME}</div>
                <div className="muted" style={{fontSize:11}}>{i18n.t('assistant.ui.contextLabel', 'context:')} {currentScreen}</div>
              </div>
            </div>
            <button className="btn btn-icon btn-ghost" onClick={() => setExpanded(false)} title={i18n.t('assistant.ui.minimize', 'Minimize')}>
              <Icon name="x" size={14}/>
            </button>
          </div>
          <div className="chatbar-msgs" ref={scrollRef}>
            {messages.map((m) => (
              <div key={m.id} className={'chatbar-row ' + (m.from === 'ai' ? 'is-ai' : 'is-u')}>
                {m.from === 'ai' && <div className="chatbar-avatar"><div className="asst-orb-mini"/></div>}
                <AssistantBubble message={m} creationAllowed={creationAllowed} onRetry={(retryText) => send(retryText, { appendUser: false })} onCancel={cancelAction} onPreviewAction={previewAction} onConfirmAction={confirmAction} onQuickReply={sendQuickReply} selectedEntity={selectedEntity}/>
              </div>
            ))}
            {thinking && (
              <div className="chatbar-row is-ai">
                <div className="chatbar-avatar"><div className="asst-orb-mini"/></div>
                <div className="chatbar-bubble is-ai chatbar-thinking">
                  <span/><span/><span/>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!creationAllowed ? <CreationAccessNotice feature="assistant" /> : <div className="chatbar-bottom">
        <div className="chatbar-inner">
          <div className="chatbar-input-row" onDrop={handleReferenceDrop} onDragOver={event => event.preventDefault()}>
            <button className="chatbar-orb-btn" onClick={() => setExpanded(!expanded)} title={ASSISTANT_NAME}>
              <div className="asst-orb-mini"/>
            </button>
            {pendingActionMetadata && (
              <button className="assistant-action-chip" type="button" onClick={() => setPendingActionMetadata(null)} title={i18n.t('assistant.ui.clearAction', 'Clear action')}>
                <span>{pendingActionMetadata.label || i18n.t('assistant.ui.draftAction', 'Draft action')}</span>
                <Icon name="x" size={11}/>
              </button>
            )}
            <AssistantReferenceChip context={referenceContext} onClear={() => setReferenceContext(null)} onUse={useReferenceContext} />
            <input
              ref={inputRef}
              className="chatbar-input"
              placeholder={assistantInputPlaceholder(pendingActionMetadata)}
              value={input}
              onChange={e => { setInput(e.target.value); if (!e.target.value.trim()) setPendingActionMetadata(null); }}
              onFocus={() => setExpanded(true)}
              onPaste={handleInputPaste}
              onKeyDown={e => { if (e.key === 'Enter' && !thinking) send(input); }}
            />
            <button className="chatbar-icon-btn" title={i18n.t('assistant.ui.attachContext', 'Attach context')} onClick={() => setAttachmentOpen(true)}><Icon name="plus" size={14}/></button>
            <button className="chatbar-send" onClick={() => send(input)} title={i18n.t('assistant.ui.send', 'Send')} disabled={thinking}>
              <Icon name="send" size={13}/>
            </button>
          </div>
        </div>
      </div>}
      <AssistantAttachmentModal
        open={attachmentOpen && creationAllowed}
        onClose={() => setAttachmentOpen(false)}
        onUploadFiles={uploadReferenceFiles}
      />
    </div>
  );
};

// ============= VOICE MODE =============
export const VoiceMode = ({ onClose }) => {
  const [listening, setListening] = useState(true);
  const [transcript, setTranscript] = useState('');
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [time, setTime] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTime(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Simulate voice conversation
  useEffect(() => {
    const phrases = [
      "Make a carousel about consistency for Instagram...",
      "Move Friday's post to Monday morning...",
      "Generate three TikTok hooks about productivity...",
    ];
    let i = 0;
    const cycle = () => {
      setListening(true);
      setAiSpeaking(false);
      let p = 0;
      const phrase = phrases[i % phrases.length];
      const typing = setInterval(() => {
        p++;
        setTranscript(phrase.slice(0, p));
        if (p >= phrase.length) {
          clearInterval(typing);
          setTimeout(() => {
            setListening(false);
            setAiSpeaking(true);
            setTimeout(() => {
              setTranscript('');
              i++;
              cycle();
            }, 2200);
          }, 600);
        }
      }, 50);
    };
    cycle();
  }, []);

  const fmtTime = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  return (
    <div className="voice-overlay">
      <div className="voice-stage">
        <div className="voice-bg-glow"/>
        <div className={'voice-orb ' + (aiSpeaking ? 'speaking' : 'listening')}>
          <div className="voice-orb-core"/>
          <div className="voice-orb-ring r1"/>
          <div className="voice-orb-ring r2"/>
          <div className="voice-orb-ring r3"/>
        </div>

        <div className="voice-status">
          {aiSpeaking ? i18n.t('assistant.ui.voiceResponding', 'AI is responding…') : listening ? i18n.t('assistant.ui.voiceListening', 'Listening…') : i18n.t('assistant.ui.voiceConnecting', 'Connecting…')}
        </div>

        <div className="voice-transcript">
          {transcript && <span className="voice-transcript-text">"{transcript}"</span>}
          {!transcript && aiSpeaking && <span className="voice-transcript-text muted">{i18n.t('assistant.ui.voiceGenerating', 'AI is generating your content...')}</span>}
        </div>

        <div className="voice-waveform">
          {Array.from({length: 32}).map((_,i) => (
            <div
              key={i}
              className="voice-wave-bar"
              style={{
                animationDelay: `${i * 0.04}s`,
                opacity: aiSpeaking ? 1 : listening ? 0.7 : 0.3
              }}
            />
          ))}
        </div>

        <div className="voice-controls">
          <button className="voice-btn voice-btn-mute" title={i18n.t('assistant.ui.mute', 'Mute')}>
            <Icon name="mic" size={20}/>
          </button>
          <button className="voice-btn voice-btn-end" onClick={onClose} title={i18n.t('assistant.ui.voiceEnd', 'End conversation')}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" transform="rotate(135 12 12)"/></svg>
          </button>
          <button className="voice-btn voice-btn-text" onClick={onClose} title={i18n.t('assistant.ui.voiceSwitchText', 'Switch to text')}>
            <Icon name="x" size={20}/>
          </button>
        </div>

        <div className="voice-time">{fmtTime(time)}</div>
      </div>
    </div>
  );
};

// ============= AI PANEL (right-side drawer) =============
export const AIPanel = ({ open, onClose, currentScreen, selectedEntity = null, notificationContext = null, onVoice, navigate }) => {
  const [messages, setMessages] = useState(() => loadCachedAssistantPanelMessages());
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [attachmentOpen, setAttachmentOpen] = useState(false);
  const [actionTrayOpen, setActionTrayOpen] = useState(false);
  const [pendingActionMetadata, setPendingActionMetadata] = useState(null);
  const [referenceContext, setReferenceContext] = useState(null);
  const { hydrateContentItem, refreshContent } = useContentFeed();
  // GenerationStatus integration — see AIChatBar above. Long-running
  // confirmActions get registered via trackAssistantResult so the
  // floating status plate tracks them globally.
  const { startOperation } = useGenerationStatus();
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const lastNotificationRequestRef = useRef(null);
  // TASK-314: Scheduler has assistant_turns_monthly=0 — show a minimal
  // locked state instead of a chat that would fail server-side. dash is
  // whatever DashboardDataProvider currently has (it fetches entitlements
  // lazily elsewhere, e.g. Settings.jsx's AccountsTab). On the first open with
  // no entitlements loaded, the effect below kicks off ONE fetch (fix #7) and
  // renders a neutral "Checking your plan…" state until it settles.
  const dash = useDashboardData() || {};
  const creationAllowed = creationAccessState(dash) === 'allowed';
  const assistantAllowance = deriveAssistantAllowance(dash.entitlements);
  // fix #7 (judge review): the panel used to lock ONLY on `allowance.zero`,
  // which fails OPEN (renders the full chat) whenever entitlements have not
  // been fetched at all — calendar/library screens never call
  // ensureDashboardData, so a Scheduler org opening the panel from there saw
  // the real chat input right up until the first send 402'd. Actively kick
  // off the fetch the first time the panel opens with no entitlements yet,
  // and track whether that check has settled (success OR failure) so the
  // render below can show a neutral "Checking your plan…" state in between,
  // instead of either guess.
  const [entitlementsChecked, setEntitlementsChecked] = useState(dash.entitlements != null);
  const entitlementsFetchStartedRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    if (dash.entitlements != null) {
      setEntitlementsChecked(true);
      return;
    }
    if (entitlementsFetchStartedRef.current) return;
    entitlementsFetchStartedRef.current = true;
    Promise.resolve(dash.ensureDashboardData?.() || {})
      .catch(() => null)
      .finally(() => setEntitlementsChecked(true));
    // dash is a fresh `{}`-or-context object every render; gating on
    // `dash.entitlements` (not the whole object) keeps this from re-firing
    // on unrelated re-renders while the fetch is in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dash.entitlements]);
  // A non-lapsed plan with a known zero allowance keeps its feature lock.
  // Unknown/lapsed access keeps history readable; the composer below is
  // replaced by a status retry or renewal notice.
  const assistantLocked = creationAllowed && assistantAllowance.known && assistantAllowance.zero;
  const assistantChecking = creationAllowed && !assistantAllowance.known && !entitlementsChecked;

  const syncAssistantCreatedContent = async (resultLike) => {
    const batchId = generationBatchIdFromAssistantResult(resultLike);
    const ids = createdContentIdsFromAssistantResult(resultLike);
    if (ids.length) {
      await Promise.all(ids.map(id => hydrateContentItem(id).catch(() => null)));
    }
    await refreshContent({ background: true, refreshMetrics: false });
    if (batchId && typeof navigate === 'function') {
      navigate('create', { batchId, replace: false });
    }
  };

  useEffect(() => {
    if (open) {
      setMessages(existing => hasConversationMessages(existing) ? existing : [greetingMessage(currentScreen)]);
    }
  }, [open, currentScreen]);

  useEffect(() => {
    cacheAssistantPanelMessages(messages);
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking]);

  useEffect(() => {
    // TASK-314: a bell-notification click auto-sends a message even though
    // the locked view below never renders the input — without this guard a
    // Scheduler org would still fire a doomed api.assistant.chat() call.
    //
    // fix #7 (judge review): gated on `assistantGate`, not the raw
    // `assistantLocked` flag — while entitlements are still being checked
    // (assistantChecking), this must ALSO suppress the auto-send (not just
    // when confirmed locked), and it must NOT record the request as handled
    // in lastNotificationRequestRef while suppressed, so the send fires once
    // the check completes and the gate opens (fail-open included) rather
    // than being silently dropped forever.
    if (!open || !notificationContext?.id || !creationAllowed) return;
    if (!assistantGate({ allowance: assistantAllowance, checked: entitlementsChecked })) return;
    const requestKey = notificationContext.requestId || `${notificationContext.id}:${notificationContext.assistantIntent || ''}`;
    if (lastNotificationRequestRef.current === requestKey) return;
    lastNotificationRequestRef.current = requestKey;
    // The visible message is plain human language (built by the bell in the
    // UI language); the intent travels as explicit actionMetadata — the same
    // mechanism quick-action buttons use — so the planner doesn't have to
    // guess the capability from prose (it used to misroute the similar-posts
    // recommendation to the reference-asset action).
    const prompt = notificationContext.displayText
      || notificationContext.inputSummary
      || notificationContext.cta
      || 'Review this notification';
    const actionMetadata = notificationContext.assistantIntent ? {
      actionId: notificationContext.recommendationId || notificationContext.notificationId || notificationContext.id,
      type: notificationContext.assistantIntent,
      label: notificationContext.cta || null,
      params: {
        ...(notificationContext.contentId ? { contentId: notificationContext.contentId } : {}),
        ...(notificationContext.platform ? { platform: notificationContext.platform } : {}),
        ...(typeof notificationContext.count === 'number' && notificationContext.count > 0 ? { count: notificationContext.count } : {}),
        ...(notificationContext.planLengthDays ? { planLengthDays: notificationContext.planLengthDays } : {}),
        ...(notificationContext.notificationId ? { notificationId: notificationContext.notificationId } : {}),
        ...(notificationContext.recommendationId ? { recommendationId: notificationContext.recommendationId } : {}),
        ...(notificationContext.inputSummary ? { inputSummary: notificationContext.inputSummary } : {}),
      },
    } : null;
    sendAssistantMessage({
      text: prompt,
      currentScreen,
      selectedEntity: notificationContext,
      setMessages,
      setInput,
      setThinking,
      appendUser: true,
      actionMetadata,
      onAssistantResult: syncAssistantCreatedContent,
    });
    // Show WHICH post the recommendation is about: a compact preview card
    // (thumbnail + snippet) fetched client-side, clickable to the full post.
    if (notificationContext.contentId) {
      const cardId = makeMessageId('srcpost');
      api.content.get(notificationContext.contentId)
        .then((res) => {
          const post = extractSourcePost(res, notificationContext.contentId);
          if (post) setMessages((prev) => [...prev, { id: cardId, from: 'ai', kind: 'sourcePost', post }]);
        })
        .catch(() => {});
    }
  }, [open, notificationContext, currentScreen, assistantAllowance.known, assistantAllowance.zero, entitlementsChecked, creationAllowed]);

  const send = (txt, options = {}) => {
    if (!creationAllowed) return Promise.resolve(null);
    const trimmed = txt.trim();
    const actionMetadata = pendingActionMetadataForMessage(trimmed, pendingActionMetadata, options);
    setActionTrayOpen(false);
    if (trimmed) setPendingActionMetadata(null);
    return sendAssistantMessage({
      text: txt,
      currentScreen,
      selectedEntity,
      actionMetadata,
      setMessages,
      setInput,
      setThinking,
      appendUser: options.appendUser !== false,
      onAssistantResult: syncAssistantCreatedContent,
    });
  };

  const sendQuickReply = (reply) => {
    const text = reply?.prompt || reply?.label || '';
    const actionMetadata = quickReplyActionMetadata(reply);
    return actionMetadata ? send(text, { actionMetadata }) : send(text);
  };

  const insertActionDraft = (draft) => {
    if (!draft?.metadata) return;
    setPendingActionMetadata(draft.metadata || null);
    setActionTrayOpen(false);
    window.requestAnimationFrame?.(() => inputRef.current?.focus());
  };

  const useReferenceContext = (kind) => {
    const draft = referenceActionDraftFromContext(referenceContext, kind);
    if (draft) insertActionDraft(draft);
  };

  const cancelConfirmation = (id) => {
    setMessages(messages => messages.map(message => message.id === id ? { ...message, cancelled: true } : message));
  };

  const setActionState = (messageId, actionIndex, state) => {
    setMessages(messages => messages.map(message => {
      if (message.id !== messageId) return message;
      const actionStates = { ...(message.actionStates || {}) };
      actionStates[actionIndex] = { ...(actionStates[actionIndex] || {}), ...state };
      return { ...message, actionStates };
    }));
  };

  const previewAction = async (messageId, actionIndex, action) => {
    setActionState(messageId, actionIndex, { status: 'previewing' });
    try {
      const preview = await api.assistant.previewAction({ action });
      setActionState(messageId, actionIndex, { status: 'previewed', preview });
    } catch (error) {
      setActionState(messageId, actionIndex, { status: 'error', message: friendlyAssistantError(error) });
    }
  };

  const confirmAction = async (messageId, actionIndex, action) => {
    if (!creationAllowed) return;
    setActionState(messageId, actionIndex, { status: 'confirming' });
    try {
      const result = await api.assistant.confirmAction({ action, confirmed: true });
      // Register long-running ops with the global status plate (see
      // AIChatBar's confirmAction for the same pattern). Safe no-op
      // for short / synchronous responses.
      try {
        trackAssistantResult(startOperation, result, action?.title);
      } catch (_) { /* best-effort */ }
      if (result?.assistantResponse) {
        setActionState(messageId, actionIndex, { status: 'confirmed', result: null });
        setMessages(messages => [...messages, assistantResponseToBubble(result.assistantResponse, result.message)]);
        notifyAssistantResult(syncAssistantCreatedContent, result.assistantResponse);
        return;
      }
      setActionState(messageId, actionIndex, { status: 'confirmed', result });
      notifyAssistantResult(syncAssistantCreatedContent, result);
    } catch (error) {
      setActionState(messageId, actionIndex, { status: 'error', message: friendlyAssistantError(error) });
    }
  };

  const cancelAction = (messageId, actionIndex) => {
    setActionState(messageId, actionIndex, { status: 'cancelled' });
  };

  const navigateFromResult = (target, options) => {
    if (typeof navigate === 'function') {
      navigate(target, options);
    }
  };

  const uploadAttachment = async (attachment) => {
    if (!creationAllowed) return null;
    setMessages(messages => [...messages, { id: makeMessageId('user'), from: 'user', text: i18n.t('assistant.ui.attachedFile', { filename: attachment.originalFilename || attachment.publicUrl || attachment.type || 'context', defaultValue: 'Attached {{filename}}' }) }]);
    try {
      const result = await api.assistant.uploadAttachment({ ...attachment, currentScreen, selectedEntity });
      const nextReferenceContext = assistantReferenceContextFromResult(attachment, result);
      if (nextReferenceContext) setReferenceContext(nextReferenceContext);
      setMessages(messages => [...messages, {
        id: makeMessageId('attachment-result'),
        from: 'ai',
        text: result?.message || i18n.t('assistant.ui.attachmentProcessed', 'Attachment processed by backend.'),
        resultCard: {
          executed: result?.status !== 'unavailable',
          message: result?.message,
          result,
          action: { type: 'reference.upload_asset', title: i18n.t('assistant.ui.referenceAttachment', 'Reference attachment') },
        },
      }]);
      const assetId = result?.asset?.id || result?.asset?.assetId;
      return assetId || null;
    } catch (error) {
      setMessages(messages => [...messages, { id: makeMessageId('attachment-error'), from: 'ai', text: friendlyAssistantError(error), error: true }]);
      return null;
    }
  };

  const uploadReferenceFiles = async (selectedFiles, notes = 'Image added by user') => {
    const followUp = input.trim();
    const assetIds = [];
    const acceptedFiles = Array.from(selectedFiles || [])
      .filter(isReferenceImageFile)
      .slice(0, MAX_ASSISTANT_IMAGES);

    for (const file of acceptedFiles) {
      try {
        const dataUrl = await fileToDataUrl(file);
        const assetId = await uploadAttachment({
          type: 'product_image',
          attachmentPurpose: 'product_image',
          dataUrl,
          originalFilename: file.name,
          mimeType: file.type || 'image/png',
          notes,
        });
        if (assetId) assetIds.push(assetId);
      } catch (error) {
        setMessages(messages => [...messages, { id: makeMessageId('attachment-error'), from: 'ai', text: friendlyAssistantError(error), error: true }]);
      }
    }

    if (!assetIds.length) {
      throw new Error(i18n.t('assistant.ui.uploadFailed', 'Upload failed. No image was saved.'));
    }

    if (followUp) {
      await sendAssistantMessage({
        text: referenceFollowUpPrompt(followUp, assetIds),
        currentScreen,
        selectedEntity: {
          type: 'referenceAsset',
          id: assetIds[0],
          ...(assetIds.length > 1 ? { inputSummary: `Reference assets: ${assetIds.join(', ')}` } : {}),
        },
        setMessages,
        setInput,
        setThinking,
        appendUser: true,
      });
    }

    return assetIds;
  };

  const uploadFileAsReference = async (file, notes = 'Dropped into Assistant') => {
    if (!isReferenceImageFile(file)) return;
    try {
      await uploadReferenceFiles([file], notes);
    } catch {
      // The upload helper already appends a visible chat error for this path.
    }
  };

  const handleInputPaste = (event) => {
    const file = Array.from(event.clipboardData?.files || []).find(isReferenceImageFile);
    if (file) {
      event.preventDefault();
      uploadFileAsReference(file, 'Pasted into Assistant');
    }
  };

  const handleReferenceDrop = (event) => {
    event.preventDefault();
    const file = Array.from(event.dataTransfer?.files || []).find(isReferenceImageFile);
    if (file) {
      uploadFileAsReference(file, 'Dropped into Assistant');
      return;
    }
    const url = extractFirstHttpUrl(event.dataTransfer?.getData('text/uri-list') || event.dataTransfer?.getData('text/plain') || '');
    if (url) setInput(existing => existing.trim() ? `${existing.trim()} ${url}` : url);
  };

  const language = assistantUiLanguage();
  const placeholder = assistantInputPlaceholder(pendingActionMetadata, language);
  const showTrayInDock = actionTrayOpen && !thinking;

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => { if (event.key === 'Escape' && !attachmentOpen) onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose, attachmentOpen]);

  const submitInput = () => {
    if (!input.trim() || thinking) return;
    send(input);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitInput();
    }
  };

  return (
    <>
      <div className={'as-bd ' + (open ? 'is-open' : '')} onClick={onClose}/>
      <aside className={'as-panel ' + (open ? 'is-open' : '')} role="dialog" aria-label={i18n.t('assistant.ui.ariaPanel', 'AI Assistant')}>
        <header className="as-panel-h">
          <div className="as-panel-h-l">
            {/* Collapse control moved to the LEFT as a directional arrow: the
                panel is docked to the right edge, so an arrow pointing right
                reads as "tuck the assistant back to the side". Replaces the
                top-right ✕ which felt like a hard "close". */}
            <button type="button" className="btn btn-icon btn-ghost btn-sm as-collapse-btn" title={i18n.t('assistant.ui.collapseAssistant', 'Collapse assistant')} aria-label={i18n.t('assistant.ui.collapseAssistant', 'Collapse assistant')} onClick={onClose}>
              <Icon name="arrowRight" size={16}/>
            </button>
            <span className="as-panel-avatar">
              <img src="/assets/dathent-logo.png" alt="" width="28" height="28" style={{ borderRadius: '50%', display: 'block' }}/>
              <span className="as-panel-online"/>
            </span>
            <div>
              <div className="as-panel-title">Dathent <span className="as-grad-text">Assistant</span></div>
              <div className="as-panel-sub">{i18n.t('assistant.ui.contextLabel', 'context:')} {currentScreen || 'app'}</div>
            </div>
          </div>
          <div className="as-panel-h-r"></div>
        </header>

        {assistantLocked ? (
          // TASK-314: Scheduler has no AI Assistant chat — a minimal locked
          // state instead of a chat input that would fail server-side (or,
          // per assistant-worker's actual behaviour, silently answer with a
          // canned "you've reached this month's limit" chat message that
          // reads wrong for a plan whose limit is permanently 0). One line +
          // one link, the same tone as textLocked/imageLocked elsewhere.
          <div className="as-thread as-locked" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: 24, textAlign: 'center' }}>
            <div>
              <p style={{ margin: 0, color: 'var(--text-2, inherit)' }}>{i18n.t('textLocked.assistantLockedLabel', 'AI Assistant is available from Starter.')}</p>
              <button
                type="button"
                className="btn btn-soft btn-sm"
                style={{ marginTop: 12 }}
                onClick={() => navigateFromResult('/app/plans')}
              >
                {i18n.t('textLocked.viewPlans', 'View plans')}
              </button>
            </div>
          </div>
        ) : assistantChecking ? (
          // fix #7 (judge review): entitlements are unknown AND the fetch
          // kicked off above has not settled yet — render neither the chat
          // (would let a Scheduler org type into a doomed request) nor the
          // locked copy (would wrongly lock a paying user whose plan just
          // has not loaded). Once the check settles this becomes either the
          // locked view (zero) or the chat (non-zero, or fail-open on error).
          <div className="as-thread as-checking" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: 24, textAlign: 'center' }}>
            <p style={{ margin: 0, color: 'var(--text-2, inherit)' }}>{i18n.t('assistant.ui.checkingPlan', 'Checking your plan…')}</p>
          </div>
        ) : (
        <>
        <div className="as-thread" ref={scrollRef} onDrop={handleReferenceDrop} onDragOver={event => event.preventDefault()}>
          {messages.map((m) => (
            m.from === 'ai' ? (
              <div key={m.id} className="as-msg as-msg-bot">
                <span className="as-avatar">
                  <img src="/assets/dathent-logo.png" alt="" width="24" height="24" style={{ borderRadius: '50%', display: 'block' }}/>
                </span>
                <AssistantBubble
                  message={m}
                  creationAllowed={creationAllowed}
                  variant="panel"
                  onRetry={(retryText) => send(retryText, { appendUser: false })}
                  onCancel={cancelAction}
                  onPreviewAction={previewAction}
                  onConfirmAction={confirmAction}
                  onQuickReply={sendQuickReply}
                  selectedEntity={selectedEntity}
                  onNavigate={navigateFromResult}
                />
              </div>
            ) : (
              <div key={m.id} className="as-msg as-msg-user">
                <AssistantBubble
                  message={m}
                  creationAllowed={creationAllowed}
                  variant="panel"
                  onRetry={(retryText) => send(retryText, { appendUser: false })}
                  onCancel={cancelAction}
                  onPreviewAction={previewAction}
                  onConfirmAction={confirmAction}
                  onQuickReply={sendQuickReply}
                  selectedEntity={selectedEntity}
                  onNavigate={navigateFromResult}
                />
              </div>
            )
          ))}
          {thinking && (
            <div className="as-msg as-msg-bot">
              <span className="as-avatar">
                <img src="/assets/dathent-logo.png" alt="" width="24" height="24" style={{ borderRadius: '50%', display: 'block' }}/>
              </span>
              <div className="as-bubble as-bubble-bot as-typing">
                <span className="as-tdot"/><span className="as-tdot"/><span className="as-tdot"/>
              </div>
            </div>
          )}
          {/* Removed the broken starter "quick chips": tapping one only typed a
              prompt and nothing ran. The proper entry point is the action tray
              below (sparkles button), which pins a real, scoped action. */}
        </div>

        {!creationAllowed ? <CreationAccessNotice feature="assistant" /> : <div className="as-dock">
          {/* Static action tray removed — the assistant is AI-driven: type a
              request in any language and the backend classifies the intent
              (create posts / connect a social account) and drives the steps. */}
          {(pendingActionMetadata || referenceContext) && (
            <div className="as-dock-meta">
              {pendingActionMetadata && (
                <button className="assistant-action-chip" type="button" onClick={() => setPendingActionMetadata(null)} title={i18n.t('assistant.ui.clearAction', 'Clear action')}>
                  <span>{pendingActionMetadata.label || i18n.t('assistant.ui.draftAction', 'Draft action')}</span>
                  <Icon name="x" size={11}/>
                </button>
              )}
              <AssistantReferenceChip context={referenceContext} onClear={() => setReferenceContext(null)} onUse={useReferenceContext} />
            </div>
          )}
          <div className="as-dock-textarea">
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={e => { setInput(e.target.value); if (!e.target.value.trim()) setPendingActionMetadata(null); }}
              onPaste={handleInputPaste}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoFocus={open}
            />
            <div className="as-dock-tools">
              <button type="button" title={i18n.t('assistant.ui.attachContext', 'Attach context')} onClick={() => setAttachmentOpen(true)}>
                <Icon name="plus" size={14}/>
              </button>
              <button type="button" className="as-send" title={i18n.t('assistant.ui.send', 'Send')} disabled={!input.trim() || thinking} onClick={submitInput}>
                <Icon name="send" size={13}/>
              </button>
            </div>
          </div>
          <div className="as-dock-foot">
            <span className="mono">⌘K</span>
            <span>{i18n.t('assistant.ui.toggleHint', 'to toggle assistant')}</span>
          </div>
        </div>}
        </>
        )}
      </aside>
      <AssistantAttachmentModal
        open={attachmentOpen && creationAllowed}
        onClose={() => setAttachmentOpen(false)}
        onUploadFiles={uploadReferenceFiles}
      />
    </>
  );
};
